<template>
  <div>
    <v-switch v-model="reloadAfterChanges" color="primary" label="Neuladen nach anpassung"></v-switch>
    <v-card-subtitle class="pt-0">
      Wenn das automatische neuladen deaktiviert ist, kann es zu Anzeigefehler kommen. Bitte dann über den Knopf neu laden.
      <v-btn color="primary" @click="$emit('fetchEntries')">Neu laden</v-btn>
    </v-card-subtitle>
    <shift-attendance-comparison
      v-if="timeEntryView && timeEntryView.user"
      :employee-id="timeEntryView.user"
      :employee-name="timeEntryView.fullName || ''"
      :studio="studio"
      :date-range="dateRange"
      :month="month"
      :company="timeEntryView.canSelfCheckin ? selectedFirma : null"></shift-attendance-comparison>

    <v-card
      v-if="!office && requiresShiftChangeReview && (allShiftChangeHistory.length || pendingShiftChangeReviewCount > 0 || shiftChangeHistoryLoading || shiftChangeHistoryError)"
      outlined
      class="pending-shift-review mb-5">
      <v-card-title class="pending-shift-review__title px-4 py-3">
        <v-avatar size="36" color="deep-orange darken-2" class="mr-3">
          <v-icon color="white" small>mdi-history</v-icon>
        </v-avatar>
        <div>
          <div class="subtitle-1 font-weight-bold">Schichtänderungen</div>
          <div class="caption grey--text text--lighten-1">Vom Mitarbeiter geänderte Schichten dieses Zeitraums prüfen und nachvollziehen</div>
        </div>
        <v-spacer></v-spacer>
        <v-chip v-if="shiftChangeHistoryLoaded && pendingShiftChangeReviewCount > 0" small color="deep-orange darken-1" text-color="white">
          {{ pendingShiftChangeReviewCount }} offen
        </v-chip>
        <v-chip v-else-if="shiftChangeHistoryLoaded" small color="success" text-color="white">
          <v-icon left x-small>mdi-check</v-icon>geprüft
        </v-chip>
        <v-chip v-else small color="grey darken-1" text-color="white">Status offen</v-chip>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-4">
        <v-alert v-if="shiftChangeHistoryError" dense text type="error" class="mb-4">{{ shiftChangeHistoryError }}</v-alert>
        <div v-else-if="shiftChangeHistoryLoading" class="py-4 text-center">
          <v-progress-circular indeterminate color="deep-orange lighten-1"></v-progress-circular>
          <div class="caption grey--text mt-2">Schichtänderungen werden geladen …</div>
        </div>
        <v-alert v-else-if="shiftChangeHistoryLoaded && unresolvedPendingShiftChangeCount > 0" dense text type="error" class="mb-4">
          {{
            unresolvedPendingShiftChangeCount === 1
              ? "Eine offene Schichtänderung konnte"
              : unresolvedPendingShiftChangeCount + " offene Schichtänderungen konnten"
          }}
          diesem Mitarbeiter noch nicht zugeordnet werden. Bitte die Übersicht neu laden.
        </v-alert>
        <v-alert v-else-if="shiftChangeHistoryLoaded && activePendingShiftChanges.length" dense text type="warning" class="mb-4">
          Bitte die Änderungen mit dem Schicht-Abgleich und den Stempelzeiten vergleichen, bevor sie gesammelt als geprüft bestätigt werden.
        </v-alert>
        <v-alert v-else-if="shiftChangeHistoryLoaded" dense text type="success" class="mb-4">
          Alle Schichtänderungen in diesem Zeitraum sind geprüft.
        </v-alert>

        <div v-for="entry in activePendingShiftChanges" :key="shiftChangeId(entry)" class="pending-shift-review__entry pa-3 mb-3">
          <div class="d-flex align-start flex-wrap mb-3">
            <div>
              <div class="font-weight-bold white--text">{{ formatShiftChangeDate(entry) }}</div>
              <div class="caption grey--text text--lighten-1">
                Geändert von {{ shiftChangeActorName(entry) }} · {{ formatShiftChangeTimestamp(entry.createdAt) }}
                <span v-if="shiftChangeStudioName(entry)"> · {{ shiftChangeStudioName(entry) }}</span>
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-chip x-small outlined color="deep-orange lighten-2">ungeprüft</v-chip>
          </div>

          <div class="pending-shift-review__reason mb-3">
            <span class="caption grey--text text--lighten-1">Grund</span>
            <div class="body-2 white--text">{{ entry.reason || "Kein Grund angegeben" }}</div>
          </div>

          <div v-for="(change, changeIndex) in shiftChangeChanges(entry)" :key="shiftChangeId(entry) + '-change-' + changeIndex" class="mb-3">
            <div class="caption font-weight-bold grey--text text--lighten-1 mb-1">{{ shiftChangeFieldLabel(change) }}</div>
            <v-row dense align="stretch">
              <v-col cols="12" sm="5">
                <div class="pending-shift-review__value pending-shift-review__value--before">
                  <span class="pending-shift-review__value-label">Vorher</span>
                  <span>{{ formatShiftChangeValue(change.before, change.field) }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="2" class="d-flex align-center justify-center">
                <v-icon color="deep-orange lighten-2">mdi-arrow-right</v-icon>
              </v-col>
              <v-col cols="12" sm="5">
                <div class="pending-shift-review__value pending-shift-review__value--after">
                  <span class="pending-shift-review__value-label">Nachher</span>
                  <span>{{ formatShiftChangeValue(change.after, change.field) }}</span>
                </div>
              </v-col>
            </v-row>
          </div>
        </div>

        <v-expansion-panels v-if="reviewedShiftChanges.length" flat accordion class="reviewed-shift-history mt-4">
          <v-expansion-panel>
            <v-expansion-panel-header class="px-3">
              <div class="d-flex align-center">
                <v-icon small color="success" class="mr-2">mdi-check-circle-outline</v-icon>
                <span class="font-weight-bold">Bereits geprüft</span>
                <v-chip x-small outlined color="success" class="ml-3">{{ reviewedShiftChanges.length }}</v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div
                v-for="entry in reviewedShiftChanges"
                :key="'reviewed-' + shiftChangeId(entry)"
                class="pending-shift-review__entry pending-shift-review__entry--reviewed pa-3 mb-3">
                <div class="d-flex align-start flex-wrap mb-3">
                  <div>
                    <div class="font-weight-bold white--text">{{ formatShiftChangeDate(entry) }}</div>
                    <div class="caption grey--text text--lighten-1">
                      Geändert von {{ shiftChangeActorName(entry) }} · {{ formatShiftChangeTimestamp(entry.createdAt) }}
                      <span v-if="shiftChangeStudioName(entry)"> · {{ shiftChangeStudioName(entry) }}</span>
                    </div>
                    <div class="caption success--text mt-1">
                      Geprüft von {{ shiftChangeReviewerName(entry) }} · {{ formatShiftChangeTimestamp(shiftChangeReviewedAt(entry)) }}
                    </div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-chip x-small outlined color="success">geprüft</v-chip>
                </div>

                <div class="pending-shift-review__reason mb-3">
                  <span class="caption grey--text text--lighten-1">Grund</span>
                  <div class="body-2 white--text">{{ entry.reason || "Kein Grund angegeben" }}</div>
                </div>

                <div v-if="shiftChangeReviewNoteFor(entry)" class="reviewed-shift-history__note caption mb-3">
                  <strong>Prüfnotiz:</strong> {{ shiftChangeReviewNoteFor(entry) }}
                </div>

                <div
                  v-for="(change, changeIndex) in shiftChangeChanges(entry)"
                  :key="'reviewed-' + shiftChangeId(entry) + '-change-' + changeIndex"
                  class="mb-3">
                  <div class="caption font-weight-bold grey--text text--lighten-1 mb-1">{{ shiftChangeFieldLabel(change) }}</div>
                  <div class="reviewed-shift-history__change body-2">
                    <span class="error--text text--lighten-2">{{ formatShiftChangeValue(change.before, change.field) }}</span>
                    <v-icon x-small color="grey lighten-1" class="mx-2">mdi-arrow-right</v-icon>
                    <span class="success--text text--lighten-2">{{ formatShiftChangeValue(change.after, change.field) }}</span>
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-textarea
          v-if="activePendingShiftChanges.length"
          v-model="shiftChangeReviewNote"
          outlined
          dense
          rows="2"
          auto-grow
          hide-details
          label="Prüfnotiz (optional)"
          class="mt-4"></v-textarea>
      </v-card-text>

      <v-card-actions v-if="activePendingShiftChanges.length" class="px-4 pb-4 pt-0">
        <v-spacer></v-spacer>
        <v-btn color="success" :loading="shiftChangeReviewLoading" @click="confirmPendingShiftChanges">
          <v-icon left small>mdi-check-all</v-icon>
          Alle als geprüft bestätigen
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-row v-if="timeEntryView.canSelfCheckin">
      <v-col cols="12">
        <v-select v-model="selectedFirma" :items="['Firma 1', 'Firma 2']" label="Firma filtern" class="mx-5" dense outlined></v-select>
        <v-btn color="success" class="ml-2" @click="exportToExcel">
          <v-icon left>mdi-file-excel</v-icon>
          Monats-Export (Zwei Firmen)
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="!editTimeEntry">
      <v-col cols="12">
        <v-card-text class="h6 pb-0" style="letter-spacing: 0.9px"><strong>Zeiterfassung</strong></v-card-text>
        <v-data-table
          :items-per-page="40"
          no-data-text="Keine Anwesenheits Einträge gefunden"
          :headers="tableHeaders"
          hide-default-footer
          :expanded.sync="expanded"
          :item-key="'date'"
          :show-expand="
            office ||
            !timeEntryView.timeTrackerMonths ||
            !timeEntryView.timeTrackerMonths.find((ttm) => ttm.month === $moment(this.month).format('YYYY-MM')) ||
            (timeEntryView.timeTrackerMonths.find((ttm) => ttm.month === $moment(this.month).format('YYYY-MM')) &&
              timeEntryView.timeTrackerMonths.find((ttm) => ttm.month === $moment(this.month).format('YYYY-MM')).state !== 'submitted')
          "
          :key="keyey"
          :single-expand="true"
          :items="tableItems">
          <template v-slot:item.date="{ item }">
            <span class="text-no-wrap">
              {{ item.date }}
              <span class="ml-2" :title="$moment(item.realDate).locale('de-ch').format('dddd')">
                {{ $moment(item.realDate).locale("de-ch").format("dd") }}
              </span>
            </span>
          </template>
          <template v-slot:item.startTime="{ item }">
            <span>
              {{ getStartTime(item.realDate, item.disableCorrection) }}
            </span>
          </template>
          <template v-slot:item.endTime="{ item }">
            <span>
              {{ getEndTime(item.realDate, item.disableCorrection) }}
            </span>
          </template>
          <template v-slot:item.duration="{ item }">
            <span>
              {{ getDurationForDay(item.realDate, item.disableCorrection).formattedTime }}
            </span>
          </template>
          <template v-slot:item.workTime="{ item }">
            <span>
              {{ getWorkDuration(item.realDate, item.disableCorrection).formattedTime }}
            </span>
          </template>
          <template v-slot:item.absence="{ item }">
            <v-tooltip bottom v-if="item.absence.note" open-delay="200" max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on" style="cursor: help; border-bottom: 1px dotted rgba(255, 255, 255, 0.3)">
                  {{ item.absence.formattedTime }}
                </span>
              </template>
              <span>{{ item.absence.note }}</span>
            </v-tooltip>
            <span v-else>
              {{ item.absence.formattedTime }}
            </span>
          </template>
          <template v-slot:item.pause="{ item }">
            <div class="d-flex align-center">
              <v-chip
                :color="getPauseChipColor(item)"
                @click="
                  () => {
                    if (item.correction !== '-') return hasCorrectionsOpenAlert()

                    if (!isMonthLocked && !checkToShortPause(item)) fixTimeDialogOptionPicker = true
                    fixTimeDialogItem = JSON.parse(JSON.stringify(item))
                  }
                ">
                {{ getPauseDuration(item.realDate, item.disableCorrection).formattedTime }}
              </v-chip>

              <v-tooltip bottom v-if="getAdjustmentInfo(item.realDate)" max-width="350" open-delay="100">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" small color="blue lighten-2" class="ml-2"> mdi-information-outline </v-icon>
                </template>
                <div class="pa-1">
                  <div class="font-weight-bold mb-1 blue--text text--lighten-3">Pause automatisch angepasst</div>
                  <div class="caption white--text mb-1">
                    <strong>Typ:</strong>
                    {{
                      getAdjustmentInfo(item.realDate).option === "extendPause"
                        ? "Pause verlängert (abgezogen)"
                        : "Pause verlängert (nach hinten verschoben)"
                    }}
                  </div>
                  <div class="caption white--text mb-1" v-if="getAdjustmentInfo(item.realDate).reason">
                    <strong>Freigabe:</strong> {{ getAdjustmentInfo(item.realDate).reason }}
                  </div>
                  <div class="caption white--text mb-1"><strong>Durch CL:</strong> {{ getAdjustmentInfo(item.realDate).by }}</div>
                  <div class="caption white--text">
                    <strong>Datum:</strong> {{ $moment(getAdjustmentInfo(item.realDate).at).format("DD.MM.YYYY HH:mm") }}
                  </div>
                </div>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="tableHeaders.length">
              <v-data-table
                hide-default-footer
                :items-per-page="40"
                no-data-text="Keine Anwesenheits Einträge gefunden"
                :headers="innerTableHeaders"
                :expanded.sync="expanded"
                :items="tableItemsForItem(item.realDate, item.disableCorrection)">
                <template v-slot:item.type="{ item }">
                  <v-tooltip bottom v-if="item.note" open-delay="200">
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip :color="mappedColors[item.absenceReason] || mappedColors[item.type]" v-bind="attrs" v-on="on">
                        {{ item.absenceReason ? mappedEntryTypes[item.absenceReason] : mappedEntryTypes[item.type] }}
                      </v-chip>
                    </template>
                    <span>{{ item.note }}</span>
                  </v-tooltip>
                  <v-chip v-else :color="mappedColors[item.absenceReason] || mappedColors[item.type]">
                    {{ item.absenceReason ? mappedEntryTypes[item.absenceReason] : mappedEntryTypes[item.type] }}
                  </v-chip>
                </template>

                <template v-slot:item.correction="{ item }">
                  <div v-if="timeEntryView.canSelfCheckin && !item.generatedCompanySplit && !item.absenceReason && item.type !== 'holiday' && !isMonthLocked">
                    <v-btn icon @click="changeCanSelfCheckinNoAdmin(item)">
                      <v-icon color="blue">mdi-pen</v-icon>
                    </v-btn>
                  </div>
                  <div v-else>
                    <v-btn icon v-if="item.type === 'sick' && !isMonthLocked" @click="deleteSickEntry(item)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                    <v-chip
                      hover
                      @click="item.correction !== 'Versteckt' && !isMonthLocked && showEmployeeCorretionDialog(item)"
                      v-if="item.correction !== '-'"
                      :color="
                        item.correction === 'Bearbeitet'
                          ? 'blue'
                          : item.correction === 'Neu'
                          ? 'success'
                          : item.correction === 'Versteckt'
                          ? 'grey'
                          : item.correction === 'Akzeptiert'
                          ? 'success'
                          : 'error'
                      ">
                      <span>{{ item.correction }}</span>
                    </v-chip>
                  </div>
                </template>
              </v-data-table>
              <v-textarea
                disabled
                color="white"
                dark
                v-if="timeEntryView.timeTrackerDailyNotes && timeEntryView.timeTrackerDailyNotes.find((noteEntry) => noteEntry.day === item.realDate)"
                :value="timeEntryView.timeTrackerDailyNotes.find((noteEntry) => noteEntry.day === item.realDate).note"></v-textarea>
            </td>
          </template>
          <template v-slot:item.correction="{ item }">
            <div v-if="timeEntryView.canSelfCheckin && !isMonthLocked">
              <v-btn icon @click="changeCanSelfCheckinNoAdmin(item, true)">
                <v-icon color="primary">mdi-plus</v-icon>
              </v-btn>
            </div>
            <v-chip
              v-if="item.correction !== '-'"
              :hover="!isMonthLocked"
              @click="
                if (!isMonthLocked) {
                  item.disableCorrection = !item.disableCorrection
                  updateIt()
                }
              "
              :color="item.disableCorrection ? 'grey' : 'orange'">
              <span>{{ item.disableCorrection ? "versteckt" : item.correction }}</span>
            </v-chip>
          </template>
        </v-data-table>
        <v-card-title>Monats Informationen</v-card-title>
        <v-row v-if="timeEntryView.overTime && !timeEntryView.noFixPercentage">
          <v-col cols="1"></v-col>
          <v-col cols="auto">Überstunden (Stand letzte abgeschlossene Zeiterfassung)</v-col>
          <v-col cols="auto">{{ timeEntryView.overTime }} Stunden</v-col>
        </v-row>
        <v-row v-if="currentMonthPercentage && !timeEntryView.noFixPercentage">
          <v-col cols="1"></v-col>
          <v-col cols="auto">Sollstunden diesen Monat</v-col>

          <v-col cols="auto">
            {{
              Math.round((calcBusinessDays - vacationDaysForMonth(studio).length) * ((8.4 * currentMonthPercentage) / 100) * 1000) / 1000 +
              " Stunden (" +
              currentMonthPercentage +
              "%)"
            }}
            <v-chip v-if="isPartialMonthEntry" color="info" x-small class="ml-2" outlined>
              Eintritt: {{ $moment(timeEntryView.entryDate).format("DD.MM.") }}
            </v-chip>
            <v-chip v-if="isPartialMonthExit" color="warning" x-small class="ml-2" outlined>
              Austritt: {{ $moment(timeEntryView.exitDate).format("DD.MM.") }}
            </v-chip>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="1"></v-col>
          <v-col cols="auto">Arbeitstage diesen Monat</v-col>

          <v-col cols="auto">
            {{ calcBusinessDays - vacationDaysForMonth(studio).length }}
            <span class="ml-2" v-if="vacationDaysForMonth(studio).length">
              ( {{ calcBusinessDays }} - {{ vacationDaysForMonth(studio).length }} Feiertag/e )
              <v-chip class="ml-1" small :key="'vacationday' + index" v-for="(vacationDay, index) of vacationDaysForMonth(studio)">{{
                vacationDay.title
              }}</v-chip>
            </span>
            <v-chip v-if="isPartialMonthEntry" color="info" small class="ml-2" outlined>
              <v-icon left small>mdi-information</v-icon>
              Reduziert (Eintritt: {{ $moment(timeEntryView.entryDate).format("DD.MM.YYYY") }})
            </v-chip>
            <v-chip v-if="isPartialMonthExit" color="warning" small class="ml-2" outlined>
              <v-icon left small>mdi-information</v-icon>
              Reduziert (Austritt: {{ $moment(timeEntryView.exitDate).format("DD.MM.YYYY") }})
            </v-chip>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="1"></v-col>
          <v-col cols="auto">Geleistete Stunden</v-col>

          <v-col cols="auto">{{ getTotalDuration.formattedTime }}</v-col>
        </v-row>
        <v-row v-if="currentMonthPercentage && !timeEntryView.noFixPercentage">
          <v-col cols="1"></v-col>
          <v-col cols="auto">Differenz (in Stunden)</v-col>
          <v-col cols="auto">
            {{
              Math.round(
                (getTotalDuration.formattedTime - (calcBusinessDays - vacationDaysForMonth(studio).length) * ((8.4 * currentMonthPercentage) / 100)) *
                  1000,
              ) / 1000
            }}
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-textarea
              @blur="updateNote()"
              v-if="(!submissionState && !office) || (submissionState && submissionState.state === 'onlyNotes')"
              label="Notiz"
              rows="2"
              auto-grow
              v-model="timeTrackerMonth.note"></v-textarea>
            <v-textarea
              v-if="(submissionState && submissionState.state === 'submitted') || office"
              label="Notiz"
              rows="2"
              auto-grow
              @blur="updateNote()"
              :disabled="!office"
              v-model="submissionState.note"></v-textarea>
            <v-textarea
              v-if="submissionState && submissionState.state === 'rejected' && !office"
              label="Notiz"
              rows="2"
              auto-grow
              @blur="updateNote()"
              v-model="submissionState.note"></v-textarea>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-if="submissionState"
              :label="submissionState ? 'Status' : ''"
              :value="
                submissionState && submissionState.state === 'submitted'
                  ? 'eingereicht'
                  : submissionState.state === 'rejected'
                  ? 'abgelehnt'
                  : submissionState.state === 'accepted'
                  ? 'abgeschlossen'
                  : 'Notiz eingetragen (noch nicht eingereicht)'
              "
              placeholder="(Status) Noch nicht eingereicht"
              disabled></v-text-field>
          </v-col>
          <v-col cols="12" class="justify-content-Center">
            <v-btn
              v-if="
                (submissionState && submissionState.state === 'rejected' && !office) ||
                (!submissionState && !office) ||
                (submissionState && submissionState.state === 'onlyNotes')
              "
              @click="submitTimeTrackerMonth()"
              color="success">
              Einreichen
            </v-btn>
          </v-col>
          <v-col cols="12" class="justify-content-Center">
            <v-btn
              class="mx-3"
              v-if="submissionState && submissionState.state === 'submitted' && office"
              @click="submitTimeTrackerMonth('rejected')"
              color="error">
              Ablehnen
            </v-btn>
            <v-btn
              v-if="submissionState && submissionState.state === 'submitted' && office"
              @click="submitTimeTrackerMonth('accepted')"
              color="success">
              Akzeptieren
            </v-btn>
          </v-col>
          <v-col cols="12" class="d-flex justify-center mt-2">
            <v-btn v-if="office" @click="exportExcelDialog = true" color="info" outlined class="mx-3">
              <v-icon left>mdi-file-excel</v-icon>
              Abrechnung exportieren (Excel)
            </v-btn>
          </v-col>
          <v-col v-if="canRepairSecondCompany" cols="12" class="d-flex justify-center">
            <v-btn outlined color="primary" @click="openSecondCompanyRepair">
              <v-icon left>mdi-autorenew</v-icon>
              Firmenaufteilung neu berechnen
            </v-btn>
          </v-col>
        </v-row>
        <v-dialog v-model="showCorrectionDialog">
          <v-card v-if="correctedItem">
            <v-card-title style="place-content: center"> Korrektur für den {{ $moment(correctedItem.realDate).format("DD.MM.YYYY") }} </v-card-title>
            <v-row no-gutters>
              <v-col
                cols="6"
                v-if="correctedItem.beforeCorrectionTime || correctedItem.beforeCorrectedTime"
                style="border-right: 3px solid grey"
                class="flex-row justify-content-center">
                <v-card-title class="error--text justify-text-center font-weight-bold" style="place-content: center"> Vor Korrektur </v-card-title>
                <v-card-title style="place-content: center">
                  {{ correctedItem.beforeCorrectionTime || correctedItem.beforeCorrectedTime + " Uhr" }}
                </v-card-title>
                <v-card-title style="place-content: center">
                  {{ correctedItem.beforeCorrectionType || correctedItem.beforeCorrectedType }}
                </v-card-title>
                <v-card-actions
                  v-if="correctedItem.type === 'checkoutCorrectionOpen' || correctedItem.type === 'checkinCorrectionOpen'"
                  class="justify-content-center">
                  <v-btn @click="rejectCorrection(correctedItem)" color="error">Ablehnen</v-btn>
                </v-card-actions>
              </v-col>
              <v-col
                :cols="correctedItem.beforeCorrectionTime || correctedItem.beforeCorrectedTime ? '6' : '12'"
                class="flex-row justify-content-center">
                <v-card-title
                  :class="{ 'error--text': correctedItem.delete === true }"
                  class="primary--text justify-text-center font-weight-bold"
                  style="place-content: center">
                  {{
                    correctedItem.beforeCorrectionTime
                      ? "Nachher"
                      : correctedItem.beforeCorrectedTime
                      ? "Aktuell"
                      : correctedItem.correction === "Abgelehnt"
                      ? "Abgelehnter Antrag"
                      : correctedItem.delete
                      ? "Gelöschter Eintrag"
                      : "Neuer Eintrag"
                  }}
                </v-card-title>
                <v-card-title style="place-content: center">
                  {{ correctedItem.time + " Uhr" }}
                </v-card-title>
                <v-card-title style="place-content: center">
                  {{ correctedItem.type.includes("checkout") ? "checkout" : "checkin" }}
                </v-card-title>
                <v-card-actions
                  v-if="
                    correctedItem.beforeCorrectionTime ||
                    correctedItem.type === 'checkoutCorrectionOpen' ||
                    correctedItem.type === 'checkinCorrectionOpen' ||
                    correctedItem.delete
                  "
                  class="justify-content-center">
                  <v-btn
                    v-if="!correctedItem.beforeCorrectionTime && !correctedItem.beforeCorrectedTime"
                    @click="rejectCorrection(correctedItem)"
                    color="error">
                    Ablehnen
                  </v-btn>

                  <v-btn @click="acceptCorrection(correctedItem)" color="primary">Akzeptieren</v-btn>
                </v-card-actions>
              </v-col>
            </v-row>
          </v-card>
          <v-card v-else>Fehler beim anzeigen bitte neu laden.</v-card>
        </v-dialog>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <office-time-tracker-entry-infos
          :employeeId="timeEntryView.employee._id"
          @fetchEntries="timeTrackerInfosUpdated"
          :entries="editTimeEntry"></office-time-tracker-entry-infos>
      </v-col>
      <v-col cols="12" class="d-flex justify-center">
        <v-btn @click="editTimeEntry = null">Zurück</v-btn>
      </v-col>
    </v-row>
    <v-dialog v-model="changeCanSelfCheckinNoAdminDialog" persistent max-width="800px">
      <v-card v-if="changeCanSelfCheckinNoAdminDialog">
        <v-card-title>Eintrag anpassen</v-card-title>
        <v-card-text>
          <v-row no-gutters>
            <v-time-picker full-width v-model="selfCheckinEntryEdit.time" format="24hr"></v-time-picker>
          </v-row>
          <v-row no-gutters>
            <v-select
              v-model="selfCheckinEntryEdit.type"
              :items="[
                { text: 'Check-In', value: 'checkin' },
                { text: 'Check-Out', value: 'checkout' },
                { text: 'Pause', value: 'pause' },
                { text: 'Pause Fertig', value: 'pauseend' },
              ]"
              label="Typ"></v-select>
          </v-row>
          <v-row class="justify-content-center">
            <v-btn color="primary" @click="saveSelfCheckinEntryEdit()">Zeit speichern</v-btn>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="fixTimeDialogOptionPicker" max-width="750px" overlay-opacity="0.85" overlay-color="#0d0d0d">
      <v-card class="rounded-xl glass-card px-6 py-6" elevation="24">
        <v-card-title class="text-h5 font-weight-bold white--text justify-center mb-2"> Pausenzeit korrigieren </v-card-title>
        <div class="text-subtitle-2 grey--text text-center mb-6">
          Die registrierte Pause ist kürzer als gesetzlich vorgeschrieben. Bitte wähle eine Option zur Anpassung aus:
        </div>

        <v-row class="justify-space-between">
          <!-- Option 1: Pause verlängern (von Arbeitszeit abziehen) -->
          <v-col cols="12" sm="6">
            <v-card
              class="d-flex flex-column justify-space-between rounded-lg border-light pa-4 btn-hover-scale"
              height="100%"
              color="rgba(255, 255, 255, 0.03)"
              dark>
              <div>
                <div class="d-flex justify-center mb-4">
                  <v-avatar size="64" color="rgba(33, 150, 243, 0.1)">
                    <v-icon color="blue" size="36">mdi-clock-minus-outline</v-icon>
                  </v-avatar>
                </div>
                <h3 class="text-h6 font-weight-bold white--text text-center mb-3">Pause verlängern</h3>
                <p class="caption grey--text text--lighten-1 text-center mb-4" style="line-height: 1.5">
                  Die Pausenzeit wird auf das gesetzliche Minimum verlängert. Die Differenz wird direkt von der täglichen Arbeitszeit abgezogen
                  (Arbeitszeit wird verkürzt).
                </p>
              </div>
              <v-card-actions class="justify-center pt-0">
                <v-btn block color="blue" class="rounded-lg text-none" @click="selectOption('extendPause')"> Auswählen </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>

          <!-- Option 2: Pause verlängern & Arbeitszeit nach hinten schieben -->
          <v-col cols="12" sm="6">
            <v-card
              class="d-flex flex-column justify-space-between rounded-lg border-light pa-4 btn-hover-scale"
              height="100%"
              color="rgba(255, 255, 255, 0.03)"
              dark>
              <div>
                <div class="d-flex justify-center mb-4">
                  <v-avatar size="64" color="rgba(255, 152, 0, 0.1)">
                    <v-icon color="orange" size="36">mdi-account-clock-outline</v-icon>
                  </v-avatar>
                </div>
                <h3 class="text-h6 font-weight-bold white--text text-center mb-2">Arbeitszeit verlängern</h3>
                <div class="text-center mb-2">
                  <v-chip x-small color="orange darken-4" text-color="white" class="font-weight-bold px-2"> Nur bei Einzelbesetzung </v-chip>
                </div>
                <p class="caption grey--text text--lighten-1 text-center mb-4" style="line-height: 1.5">
                  Die Pausenzeit wird verlängert, aber die gearbeiteten Stunden bleiben gleich. Die Checkout-Zeit des Mitarbeiters wird entsprechend
                  nach hinten verschoben.
                </p>
              </div>
              <v-card-actions class="justify-center pt-0">
                <v-btn block color="orange darken-2" class="rounded-lg text-none" @click="selectOption('extendWorktime')">
                  Auswählen & Bestätigen
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <v-card-actions class="justify-center mt-4">
          <v-btn text color="grey" class="text-none" @click="fixTimeDialogOptionPicker = false"> Abbrechen </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Zweite Abfrage für Einzelbesetzung (Arbeitszeit hinten verlängern) -->
    <v-dialog v-model="soloShiftConfirmDialog" max-width="500px" overlay-opacity="0.90" overlay-color="#050505" persistent>
      <div style="position: relative; padding-top: 45px">
        <div class="d-flex justify-center" style="position: absolute; top: 0; left: 0; right: 0; z-index: 2">
          <v-avatar size="90" class="elevation-10 shadow-glow-orange icon-ring" style="background: linear-gradient(135deg, #ff9800 0%, #e65100 100%)">
            <v-icon x-large color="white">mdi-alert-decagram-outline</v-icon>
          </v-avatar>
        </div>

        <v-card class="rounded-xl glass-card text-center pb-6 pt-10 px-6 overflow-visible" elevation="24">
          <div
            style="
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              height: 120px;
              background: linear-gradient(to bottom, rgba(255, 152, 0, 0.15) 0%, transparent 100%);
              z-index: 0;
              border-radius: 24px 24px 0 0;
            "></div>

          <div style="position: relative; z-index: 1">
            <h3 class="text-h5 font-weight-bold white--text mb-2 mt-4">Einzelbesetzung bestätigen</h3>
            <div class="text-subtitle-2 orange--text text--lighten-2 mb-4">Besondere betriebliche Voraussetzung</div>

            <p class="body-2 grey--text text--lighten-1 mb-6 text-left" style="line-height: 1.6">
              Die Anpassung der Checkout-Zeit nach hinten (unter Beibehaltung der Arbeitszeit) ist gesetzlich nur zulässig, wenn die Person während
              der Pause eine <strong>Einzelbesetzung</strong> hatte und zwingend vor Ort Kunden (z.B. an der Theke) bedienen musste.
            </p>

            <v-sheet color="rgba(255,152,0,0.05)" class="rounded-lg pa-4 mb-6 border-light text-left">
              <v-checkbox v-model="soloShiftConfirmed" color="orange" dark hide-details class="mt-0 pt-0">
                <template v-slot:label>
                  <span class="body-2 grey--text text--lighten-2" style="user-select: none; line-height: 1.4">
                    Ich bestätige, dass es sich um eine Einzelbesetzung handelte und dies nicht anders gelöst werden konnte.
                  </span>
                </template>
              </v-checkbox>
            </v-sheet>

            <v-row dense>
              <v-col cols="6">
                <v-btn block text large color="grey" class="rounded-lg text-none" @click="cancelSoloShiftConfirm"> Abbrechen </v-btn>
              </v-col>
              <v-col cols="6">
                <v-btn
                  block
                  large
                  color="orange"
                  class="rounded-lg text-none font-weight-bold white--text"
                  :disabled="!soloShiftConfirmed"
                  @click="confirmSoloShift">
                  Bestätigen & Weiter
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card>
      </div>
    </v-dialog>

    <v-dialog v-model="fixTimeDialog" max-width="800px" overlay-opacity="0.85" overlay-color="#0d0d0d">
      <v-card class="rounded-xl glass-card px-6 py-6" elevation="24" v-if="preSavedAttendances">
        <v-card-title class="text-h5 font-weight-bold white--text justify-center mb-2"> Pausenanpassung prüfen </v-card-title>
        <div class="text-subtitle-2 grey--text text-center mb-6">Bitte vergleiche die bisherigen Zeiteinträge mit dem korrigierten Vorschlag:</div>

        <v-row class="align-center mb-4">
          <!-- Bisherige Einträge -->
          <v-col cols="12" md="5">
            <v-card
              class="rounded-lg pa-4 text-left border-light"
              color="rgba(244, 67, 54, 0.05)"
              style="border-color: rgba(244, 67, 54, 0.2) !important">
              <div class="subtitle-2 font-weight-bold red--text text--lighten-2 mb-3 d-flex align-center">
                <v-icon small color="red lighten-2" class="mr-2">mdi-alert-circle-outline</v-icon>
                Bisherige Zeiteinträge
              </div>
              <v-sheet color="transparent" class="d-flex flex-column" style="gap: 8px">
                <div
                  v-for="(att, key) in preSavedAttendances.old"
                  class="d-flex align-center justify-space-between py-1"
                  :key="'att_old' + key"
                  style="border-bottom: 1px dashed rgba(255, 255, 255, 0.05)">
                  <v-chip small :color="mappedColors[att.type]" class="font-weight-bold">
                    {{ mappedEntryTypes[att.type] }}
                  </v-chip>
                  <span class="white--text font-weight-bold body-2">{{ att.time }}</span>
                </div>
              </v-sheet>
            </v-card>
          </v-col>

          <!-- Pfeil -->
          <v-col cols="12" md="2" class="text-center py-2">
            <v-avatar color="rgba(255, 255, 255, 0.05)" size="48">
              <v-icon color="primary" large class="d-none d-md-inline">mdi-arrow-right-bold</v-icon>
              <v-icon color="primary" large class="d-md-none">mdi-arrow-down-bold</v-icon>
            </v-avatar>
          </v-col>

          <!-- Neue Einträge -->
          <v-col cols="12" md="5">
            <v-card
              class="rounded-lg pa-4 text-left border-light"
              color="rgba(76, 175, 80, 0.05)"
              style="border-color: rgba(76, 175, 80, 0.2) !important">
              <div class="subtitle-2 font-weight-bold green--text text--lighten-2 mb-3 d-flex align-center">
                <v-icon small color="green lighten-2" class="mr-2">mdi-check-circle-outline</v-icon>
                Vorschlag (Korrigiert)
              </div>
              <v-sheet color="transparent" class="d-flex flex-column" style="gap: 8px">
                <div
                  v-for="(att, key) in preSavedAttendances.new"
                  class="d-flex align-center justify-space-between py-1"
                  :key="'att_new' + key"
                  style="border-bottom: 1px dashed rgba(255, 255, 255, 0.05)">
                  <v-chip small :color="mappedColors[att.type]" class="font-weight-bold">
                    {{ mappedEntryTypes[att.type] }}
                  </v-chip>
                  <span class="white--text font-weight-bold body-2">{{ att.time }}</span>
                </div>
              </v-sheet>
            </v-card>
          </v-col>
        </v-row>

        <!-- Warnhinweis bei Einzelbesetzung -->
        <v-alert
          v-if="preSavedAttendances.adjustmentInfo && preSavedAttendances.adjustmentInfo.option === 'extendWorktime'"
          color="rgba(255, 152, 0, 0.1)"
          class="rounded-lg border-light mb-6 text-left"
          style="border-color: rgba(255, 152, 0, 0.2) !important">
          <div class="d-flex align-center">
            <v-icon color="orange" small class="mr-3">mdi-shield-check-outline</v-icon>
            <div class="caption grey--text text--lighten-1">
              <strong>Freigabe:</strong> Einzelbesetzung wurde bestätigt. Diese Buchung wird mit einem entsprechenden Freigabe-Vermerk gespeichert.
            </div>
          </div>
        </v-alert>

        <v-row class="justify-center mt-2" dense>
          <v-col cols="6" sm="4">
            <v-btn block large text color="grey" class="rounded-lg text-none" @click="cancelFixTimeDialog"> Abbrechen </v-btn>
          </v-col>
          <v-col cols="6" sm="6">
            <v-btn block large color="primary" class="rounded-lg text-none font-weight-bold white--text" @click="savePreSavedAttendances">
              Änderungen Speichern
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showSubmitOfficeDialog" max-width="500" overlay-opacity="0.90" overlay-color="#050505">
      <div style="position: relative; padding-top: 45px">
        <div class="d-flex justify-center" style="position: absolute; top: 0; left: 0; right: 0; z-index: 2">
          <v-avatar size="90" class="elevation-10 shadow-glow icon-ring" style="background: linear-gradient(135deg, #00963f 0%, #005c27 100%)">
            <v-icon x-large color="white">mdi-shield-check-outline</v-icon>
          </v-avatar>
        </div>

        <v-card class="rounded-xl glass-card text-center pb-6 pt-10 px-6 overflow-visible" elevation="24">
          <div
            style="
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              height: 120px;
              background: linear-gradient(to bottom, rgba(0, 150, 63, 0.15) 0%, transparent 100%);
              z-index: 0;
              border-radius: 24px 24px 0 0;
            "></div>

          <div style="position: relative; z-index: 1">
            <h3 class="text-h5 font-weight-bold white--text mb-1 mt-4">Monatsabschluss bestätigen</h3>
            <div class="text-subtitle-2 grey--text mb-6">{{ timeEntryView.fullName }}</div>

            <v-sheet color="rgba(255,255,255,0.05)" class="rounded-lg pa-4 mb-6 border-light">
              <v-row dense>
                <v-col :cols="timeEntryView.noFixPercentage ? '12' : '6'" class="text-left">
                  <div class="text-caption grey--text">Geleistet</div>
                  <div class="font-weight-bold white--text text-h6">{{ submitOfficeData.duration }} h</div>
                </v-col>
                <v-col v-if="!timeEntryView.noFixPercentage" cols="6" class="text-right">
                  <div class="text-caption grey--text">Differenz</div>
                  <div class="font-weight-bold text-h6" :class="parseFloat(submitOfficeData.difference) >= 0 ? 'success--text' : 'error--text'">
                    {{ submitOfficeData.difference }} h
                  </div>
                </v-col>
                <v-col v-if="!timeEntryView.noFixPercentage" cols="12" class="mt-2 pt-2" style="border-top: 1px solid rgba(255, 255, 255, 0.1)">
                  <div class="d-flex justify-space-between align-center">
                    <span class="text-caption grey--text">Überstunden Saldo (Neu)</span>
                    <span class="font-weight-bold white--text">{{ submitOfficeData.newOvertime }} h</span>
                  </div>
                </v-col>
              </v-row>
            </v-sheet>

            <v-alert color="rgba(255, 152, 0, 0.15)" class="rounded-lg text-left mb-6" style="border: 1px solid rgba(255, 152, 0, 0.3)">
              <div class="d-flex align-start">
                <v-icon color="orange" small class="mt-1 mr-3">mdi-alert-decagram-outline</v-icon>
                <div class="caption grey--text text--lighten-1" style="line-height: 1.4">
                  Mit der Einreichung der Stundenübersicht des Mitarbeiters, bestätigt der Vorgesetzte diese
                  <strong class="white--text">kontrolliert zu haben</strong> und bestätigt deren <strong class="white--text">Richtigkeit</strong>.
                </div>
              </div>
            </v-alert>

            <v-alert
              v-if="!office && requiresShiftChangeReview"
              dense
              text
              :type="!shiftChangeHistoryLoaded || pendingShiftChangeReviewCount > 0 ? 'error' : 'success'"
              class="rounded-lg text-left mb-6">
              <strong v-if="!shiftChangeHistoryLoaded">
                {{
                  shiftChangeHistoryLoading
                    ? "Prüfstatus der Schichtänderungen wird geladen."
                    : shiftChangeHistoryError || "Prüfstatus nicht verfügbar."
                }}
              </strong>
              <strong v-else-if="pendingShiftChangeReviewCount > 0">
                {{ pendingShiftChangeReviewCount }} ungeprüfte Schichtänderung{{ pendingShiftChangeReviewCount === 1 ? "" : "en" }} offen.
              </strong>
              <strong v-else>Schichtänderungen geprüft – keine offen.</strong>
              <div v-if="!shiftChangeHistoryLoaded" class="caption mt-1">
                Der Monatsabschluss bleibt gesperrt, bis der Prüfstatus geladen werden konnte.
              </div>
              <div v-else-if="pendingShiftChangeReviewCount > 0" class="caption mt-1">
                Bitte zuerst im Prüfblock alle Änderungen als geprüft bestätigen.
              </div>
            </v-alert>

            <v-row dense>
              <v-col cols="5">
                <v-btn block large text color="grey" class="rounded-lg" @click="showSubmitOfficeDialog = false"> Abbrechen </v-btn>
              </v-col>
              <v-col cols="7">
                <v-btn
                  block
                  large
                  color="#00963f"
                  class="rounded-lg white--text shadow-glow btn-hover-scale"
                  @click="confirmOfficeSubmission"
                  :loading="submitLoading"
                  :disabled="!office && requiresShiftChangeReview && (!shiftChangeHistoryLoaded || pendingShiftChangeReviewCount > 0)">
                  <v-icon left small>mdi-check-all</v-icon>
                  Bestätigen
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card>
      </div>
    </v-dialog>
    <!-- EXCEL EXPORT DIALOG -->
    <v-dialog v-model="exportExcelDialog" max-width="500">
      <v-card color="#1e1e1e" dark>
        <v-card-title>Abrechnung exportieren</v-card-title>
        <v-card-text>
          <p>Wähle den Zeitraum für den Excel-Export aus.</p>
          <v-row>
            <v-col cols="6">
              <v-menu v-model="menuExportStart" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="exportStartMonth" label="Von (Monat)" readonly v-bind="attrs" v-on="on"></v-text-field>
                </template>
                <v-date-picker v-model="exportStartMonth" type="month" @input="menuExportStart = false"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="6">
              <v-menu v-model="menuExportEnd" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="exportEndMonth" label="Bis (Monat)" readonly v-bind="attrs" v-on="on"></v-text-field>
                </template>
                <v-date-picker v-model="exportEndMonth" type="month" @input="menuExportEnd = false"></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text color="grey" @click="exportExcelDialog = false">Abbrechen</v-btn>
          <v-btn color="success" :loading="exportingExcel" @click="generateAndDownloadExcel">
            <v-icon left>mdi-download</v-icon> Herunterladen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="secondCompanyRepairDialog" persistent max-width="600">
      <v-card>
        <v-card-title>Firmenaufteilung neu berechnen</v-card-title>
        <v-card-text>
          <p>{{ timeEntryView.fullName }} · {{ $moment(month).format("MM.YYYY") }}</p>
          <p>Die Arbeitszeit dieses Monats wird anhand der Stempelzeiten erneut auf Firma 1 und Firma 2 verteilt. Pausen bleiben erhalten.</p>
          <v-alert v-if="secondCompanyRepairError" type="error">{{ secondCompanyRepairError }}</v-alert>
          <template v-if="secondCompanyRepairResult">
            <v-alert type="success">
              {{ secondCompanyRepairResult.processedDays }} Tage geprüft, {{ secondCompanyRepairResult.changedDays }} Tage aktualisiert.
            </v-alert>
            <v-alert v-for="day in secondCompanyRepairResult.details.filter((entry) => entry.status !== 'ok')" :key="day.day" type="warning">
              {{ $moment(day.day).format("DD.MM.YYYY") }}:
              {{ day.status === 'invalid' ? 'Unstimmige Stempelzeiten – Tag unverändert. Bitte zuerst die Zeiten korrigieren.' : 'Offener Arbeitsblock – abgeschlossen erfasste Arbeitszeit wurde zugeordnet.' }}
            </v-alert>
          </template>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text :disabled="secondCompanyRepairLoading" @click="closeSecondCompanyRepair">
            {{ secondCompanyRepairAttempted ? 'Schliessen und neu laden' : 'Abbrechen' }}
          </v-btn>
          <v-btn v-if="!secondCompanyRepairResult" color="primary" :loading="secondCompanyRepairLoading" @click="repairSecondCompany">
            Neu berechnen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import officeTimeTrackerEntryInfos from "./OfficeTimeTrackerEntryInfos.vue"
import ShiftAttendanceComparison from "./ShiftAttendanceComparison.vue"
import api from "@/api"
import userService from "@/services/userService"
import { mapState } from "vuex"

export default {
  name: "OfficeTimeTrackerView",
  props: {
    office: {
      default: false,
      type: Boolean,
    },
    month: {
      required: true,
    },
    dateRange: {
      type: Array,
      default: () => [],
    },
    shiftChangeHistory: {
      type: Array,
      default: () => [],
    },
    shiftChangeHistoryPendingCount: {
      type: Number,
      default: 0,
    },
    shiftChangeHistoryLoaded: {
      type: Boolean,
      default: false,
    },
    shiftChangeHistoryLoading: {
      type: Boolean,
      default: false,
    },
    shiftChangeHistoryError: {
      type: String,
      default: "",
    },
    timeEntryView: {
      type: Object,
      required: true,
    },
    studio: {
      required: true,
    },
  },
  components: { officeTimeTrackerEntryInfos, ShiftAttendanceComparison },
  data() {
    return {
      holidaysData: [],
      secondCompanyRepairDialog: false,
      secondCompanyRepairLoading: false,
      secondCompanyRepairAttempted: false,
      secondCompanyRepairResult: null,
      secondCompanyRepairError: "",
      notes: [],
      calendarType: "month",
      showSubmitOfficeDialog: false,
      submitLoading: false,
      submitOfficeData: {
        duration: 0,
        difference: 0,
        overtimeBefore: 0,
        newOvertime: 0,
        targetState: "submitted",
      },
      selectedFirma: "Firma 1", // HINZUGEFÜGT: Standardfilter

      fixTimeDialogItem: null,
      fixTimeDialogOptionPicker: false,
      fixTimeDialog: false,
      soloShiftConfirmDialog: false,
      soloShiftConfirmed: false,
      selectedOption: null,
      preSavedAttendances: null,
      reloadAfterChanges: true,
      timeTrackerMonth: {
        note: "",
      },
      correctedItem: null,
      showCorrectionDialog: false,
      mapHolidayStatus: {
        open: "Offen",
        accepted: "Akzeptiert",
        denied: "Abgelehnt",
      },
      exportExcelDialog: false,
      menuExportStart: false,
      menuExportEnd: false,
      exportStartMonth: this.$moment(this.month).format("YYYY-MM"),
      exportEndMonth: this.$moment(this.month).format("YYYY-MM"),
      exportingExcel: false,
      shiftChangeReviewNote: "",
      shiftChangeReviewLoading: false,
      locallyReviewedShiftChangeIds: [],
      localShiftReviewMeta: {},
      mappedEntryTypes: {
        technicCheckin: "Checkin Badge",
        technicCheckout: "Checkout Badge",
        sick: "Krank",
        school: "Schule",
        pregnancy: "Schwangerschaft",
        other: "Anderes",
        military: "Militär",
        holiday: "Ferien",
        holidayFix: "FERIEN OHNE PENSUM ABZUG",
        checkin: "Check-In",
        checkout: "Check-Out",
        pause: "Pause",
        pauseend: "Pause fertig",
        error: "Fehler (Checkin ohne Checkout)",
        error1: "Fehler (2 Check-Ins hintereinander)",
        error2: "Fehler (2 Check-Outs hintereinander)",
        error3: "Fehler (Checkout ohne Checkin)",
        deleteCorrectionOpen: "Löschen (Korrektur offen)",
        checkinCorrectionOpen: "Check-In (Korrektur offen)",
        checkinCorrectionDenied: "Check-In (Korrektur abgelehnt)",
        checkinCorrectionApproved: "Check-In (Korrektur genehmigt)",
        checkoutCorrectionOpen: "Check-Out (Korrektur offen)",
        checkoutCorrectionDenied: "Check-Out (Korrektur abgelehnt)",
        checkoutCorrectionApproved: "Check-Out (Korrektur genehmigt)",
      },
      keyey: 0,
      mappedColors: {
        technicCheckin: "green",
        technicCheckout: "red",
        sick: "orange",
        school: "blue",
        military: "purple",
        pregnancy: "teal",
        other: "grey",
        holiday: "orange",
        checkin: "success",
        checkout: "grey",
        pause: "blue",
        pauseend: "blue",
        error: "red",
        error1: "red",
        error2: "red",
        error3: "red",
        deleteCorrectionOpen: "red",
        checkinCorrectionOpen: "orange",
        checkinCorrectionDenied: "red",
        checkinCorrectionApproved: "success",
        checkoutCorrectionOpen: "orange",
        checkoutCorrectionDenied: "red",
        checkoutCorrectionApproved: "success",
      },
      tableHeaders: [
        { text: "Datum", value: "date" },
        { text: "Startzeit", value: "startTime" },
        { text: "Endzeit", value: "endTime" },
        { text: "Abwesend", value: "absence" },
        { text: "Dauer", value: "duration" },
        { text: "Pause", value: "pause" },
        { text: "Gearbeitet", value: "workTime" },
        { text: "Korrektur", value: "correction" },
        { text: "", value: "data-table-expand" },
      ],
      innerTableHeaders: [
        { text: "Uhrzeit", value: "time", sortable: false },
        { text: "Typ", value: "type", sortable: false },
        { text: "Studio", value: "studio", sortable: false },
        { text: "Dauer (Abwesenheit)", value: "duration", sortable: false },
        { text: "Korrektur", value: "correction", sortable: false },
        // { text: 'Aktion', value: 'actions', sortable: false },
      ],
      expanded: [],
      editTimeEntry: null,
      selfCheckinEntryEdit: null,
      changeCanSelfCheckinNoAdminDialog: false,
    }
  },
  async created() {
    if (!this.holidaysData.length && api.fetchHolidays) {
      try {
        this.holidaysData = await api.fetchHolidays()
      } catch (e) {
        console.error("Error fetching holidays", e)
      }
    }
  },
  watch: {
    submissionState: {
      immediate: true,
      handler(val) {
        if (val && val.note !== undefined) {
          this.timeTrackerMonth.note = val.note
        }
      },
    },
  },
  computed: {
    canRepairSecondCompany() {
      return this.office && this.timeEntryView.secondCompany && (userService.hasRight("tt_office") || userService.hasRight("tt_management"))
    },
    ...mapState("studioStore", ["storeStudios"]),
    requiresShiftChangeReview() {
      return this.timeEntryView?.requiresShiftChangeReview !== false
    },
    allShiftChangeHistory() {
      return this.shiftChangeHistory || []
    },
    activePendingShiftChanges() {
      const locallyReviewed = new Set(this.locallyReviewedShiftChangeIds.map((id) => String(id)))
      return this.allShiftChangeHistory.filter((entry) => entry.reviewStatus === "pending" && !locallyReviewed.has(this.shiftChangeId(entry)))
    },
    reportedPendingShiftChangeCount() {
      const count = Number(this.shiftChangeHistoryPendingCount)
      return Number.isFinite(count) && count > 0 ? count : 0
    },
    pendingShiftChangeReviewCount() {
      return Math.max(this.reportedPendingShiftChangeCount, this.activePendingShiftChanges.length)
    },
    unresolvedPendingShiftChangeCount() {
      return Math.max(0, this.reportedPendingShiftChangeCount - this.activePendingShiftChanges.length)
    },
    reviewedShiftChanges() {
      const locallyReviewed = new Set(this.locallyReviewedShiftChangeIds.map((id) => String(id)))
      return this.allShiftChangeHistory.filter((entry) => entry.reviewStatus === "reviewed" || locallyReviewed.has(this.shiftChangeId(entry)))
    },
    filteredAttendance() {
      if (!this.timeEntryView.attendance || !this.timeEntryView.canSelfCheckin) {
        return this.timeEntryView.attendance
      }

      // Absence types should never be filtered out by company
      const absenceTypes = ["sick", "holiday", "military", "school", "pregnancy", "other", "holidayFix"]

      const filtered = {}
      for (const date in this.timeEntryView.attendance) {
        const entriesForDate = this.timeEntryView.attendance[date]
        if (Array.isArray(entriesForDate)) {
          filtered[date] = entriesForDate.filter((entry) => {
            // Always keep absence entries regardless of company
            if (absenceTypes.includes(entry.type)) return true
            const company = entry.company || "Firma 1"
            return company === this.selectedFirma
          })
        }
      }
      return filtered
    },

    submissionState() {
      if (!this.timeEntryView || !this.timeEntryView.timeTrackerMonths) return null
      return this.timeEntryView.timeTrackerMonths.find((ttm) => ttm.month === this.$moment(this.month).format("YYYY-MM")) || null
    },
    currentMonthPercentage() {
      if (!this.timeEntryView) return 0
      if (this.timeEntryView.noFixPercentage) return 0
      let targetFormat = this.$moment(this.month).format("YYYY-MM")

      // Use allStudios if available to get the studio-spanning/main studio percentage
      let studios = this.timeEntryView.allStudios || this.timeEntryView.studio || []

      // Find main studio entry or first studio entry
      let studioEntry = studios.find((s) => s.mainStudio)
      if (!studioEntry && studios.length) {
        studioEntry = studios[0]
      }

      let history = (studioEntry && studioEntry.pensumHistory) || []
      if (!history.length) {
        let withHistory = studios.find((s) => s.pensumHistory && s.pensumHistory.length > 0)
        if (withHistory) {
          history = withHistory.pensumHistory
        }
      }
      let sortedHistory = history.slice().sort((a, b) => b.validFrom.localeCompare(a.validFrom))
      let applicable = sortedHistory.find((h) => h.validFrom <= targetFormat)
      if (applicable) return applicable.percentage
      // Use only base percentage (Zeiterfassung), never statistics percentage
      return this.timeEntryView.percentage !== undefined && this.timeEntryView.percentage !== null ? this.timeEntryView.percentage : 0
    },
    calcBusinessDays() {
      var day = this.$moment(this.month).startOf("month")
      var businessDays = 0

      let entryDate = this.timeEntryView.entryDate ? this.$moment(this.timeEntryView.entryDate, "YYYY-MM-DD") : null
      let exitDate = this.timeEntryView.exitDate ? this.$moment(this.timeEntryView.exitDate, "YYYY-MM-DD") : null

      while (day.isSameOrBefore(this.$moment(this.month).endOf("month"), "day")) {
        let isAfterEntry = !entryDate || day.isSameOrAfter(entryDate, "day")
        let isBeforeExit = !exitDate || day.isSameOrBefore(exitDate, "day")

        if (day.day() != 0 && day.day() != 6 && isAfterEntry && isBeforeExit) {
          businessDays++
        }
        day.add(1, "d")
      }
      return businessDays
    },
    isPartialMonthEntry() {
      if (!this.timeEntryView || !this.timeEntryView.entryDate) return false
      let entry = this.$moment(this.timeEntryView.entryDate)
      let monthStart = this.$moment(this.month).startOf("month")
      let monthEnd = this.$moment(this.month).endOf("month")
      return entry.isAfter(monthStart) && entry.isSameOrBefore(monthEnd)
    },
    isPartialMonthExit() {
      if (!this.timeEntryView || !this.timeEntryView.exitDate) return false
      let exit = this.$moment(this.timeEntryView.exitDate)
      let monthStart = this.$moment(this.month).startOf("month")
      let monthEnd = this.$moment(this.month).endOf("month")
      return exit.isBefore(monthEnd) && exit.isSameOrAfter(monthStart)
    },
    tableItems() {
      let items = []
      let includesCorrection = false
      if (!this.filteredAttendance) return items
      for (const date of Object.keys(this.filteredAttendance).filter((key) => key !== "email" && key !== "_id")) {
        includesCorrection = this.timeEntryView.attendance[date].find((entry) => entry.correction && entry.correction === "open")
        items.push({
          realDate: date,
          date: this.$moment(date).format("DD.MM."),
          startTime: "",
          endTime: "",
          absence: this.getAbsenceDuration(this.timeEntryView.attendance[date], date),
          workTime: "",
          pause: "",
          duration: "",
          correction: includesCorrection ? "Offen" : "-",
        })
      }
      return items
    },
    getTotalDuration() {
      return this.getHolidayDurationSum
    },
    isMonthLocked() {
      // Wenn es die Admin-Ansicht (office) ist, ist es nie gesperrt für den Admin
      if (this.office) return false

      // Ansonsten (Centerleiter) sperren wir, wenn der Monat 'submitted' oder 'accepted' ist.
      if (this.submissionState && (this.submissionState.state === "submitted" || this.submissionState.state === "accepted")) {
        return true
      }
      return false
    },
    getHolidayDurationSum() {
      let totalMinutes = 0

      if (!this.filteredAttendance) return { formattedTime: 0, totalMinutes: 0 }
      Object.keys(this.filteredAttendance).forEach((date) => {
        totalMinutes += this.getWorkDuration(date).totalWorkTime
      })

      let hours = totalMinutes / 60 // Stunden berechnen

      let formattedTime = parseFloat(hours.toFixed(2))

      return { formattedTime, totalMinutes }
    },
  },
  methods: {
    openSecondCompanyRepair() {
      this.secondCompanyRepairResult = null
      this.secondCompanyRepairError = ""
      this.secondCompanyRepairAttempted = false
      this.secondCompanyRepairDialog = true
    },
    closeSecondCompanyRepair() {
      this.secondCompanyRepairDialog = false
      if (this.secondCompanyRepairAttempted) this.$emit("fetchEntries")
    },
    async repairSecondCompany() {
      if (this.secondCompanyRepairLoading || !this.canRepairSecondCompany) return
      this.secondCompanyRepairLoading = true
      this.secondCompanyRepairAttempted = true
      this.secondCompanyRepairError = ""
      try {
        this.secondCompanyRepairResult = await api.repairTimetrackerSecondCompany(this.timeEntryView.user, this.$moment(this.month).format("YYYY-MM"))
      } catch (error) {
        this.secondCompanyRepairError = error.response?.data?.message || "Die Firmenaufteilung konnte nicht vollständig neu berechnet werden. Bitte neu laden und erneut versuchen."
      } finally {
        this.secondCompanyRepairLoading = false
      }
    },
    shiftChangeId(entry) {
      return String((entry && (entry._id || entry.id)) || "")
    },
    shiftChangeActorName(entry) {
      const actor = entry && entry.actor
      if (entry && entry.actorName) return entry.actorName
      if (!actor) return "Mitarbeiter"
      if (typeof actor === "string") return actor
      return (
        actor.fullName ||
        actor.name ||
        [actor.vorname || actor.firstName, actor.nachname || actor.lastName].filter(Boolean).join(" ") ||
        "Mitarbeiter"
      )
    },
    shiftChangeStudioName(entry) {
      const studio = entry && entry.studio
      return studio && typeof studio === "object" ? studio.name || "" : ""
    },
    shiftChangeReviewerName(entry) {
      if (entry && (entry.reviewedByName || entry.reviewerName)) return entry.reviewedByName || entry.reviewerName
      const reviewer = entry && entry.reviewedBy
      if (reviewer && typeof reviewer === "object") {
        return (
          reviewer.fullName ||
          reviewer.name ||
          [reviewer.vorname || reviewer.firstName, reviewer.nachname || reviewer.lastName].filter(Boolean).join(" ") ||
          "Vorgesetzter"
        )
      }
      return this.localShiftReviewMeta[this.shiftChangeId(entry)] ? "Du" : "Vorgesetzter"
    },
    shiftChangeReviewedAt(entry) {
      return (entry && entry.reviewedAt) || this.localShiftReviewMeta[this.shiftChangeId(entry)]?.reviewedAt || null
    },
    shiftChangeReviewNoteFor(entry) {
      return (entry && (entry.reviewNote || entry.reviewedNote)) || this.localShiftReviewMeta[this.shiftChangeId(entry)]?.reviewNote || ""
    },
    formatShiftChangeDate(entry) {
      const date = entry && (entry.date || entry.shiftDate || entry.after?.date || entry.before?.date)
      return date ? `Schicht vom ${this.$moment(date).format("DD.MM.YYYY")}` : "Schichtdatum unbekannt"
    },
    formatShiftChangeTimestamp(value) {
      if (!value) return "Zeitpunkt unbekannt"
      const timestamp = this.$moment(value)
      return timestamp.isValid() ? timestamp.format("DD.MM.YYYY, HH:mm") : "Zeitpunkt unbekannt"
    },
    shiftChangeChanges(entry) {
      if (entry && Array.isArray(entry.changes) && entry.changes.length) return entry.changes
      if (entry && (entry.before || entry.after)) {
        return [{ field: "times", label: "Schicht", before: entry.before, after: entry.after }]
      }
      return [{ field: "unknown", label: "Änderung", before: null, after: null }]
    },
    shiftChangeFieldLabel(change) {
      if (change && change.label) return change.label
      const field = change && change.field
      return (
        {
          times: "Schichtzeiten",
          isOpeningShift: "Öffnungsdienst",
          openingTime: "Centeröffnung",
          isClosingShift: "Schliessdienst",
          closingTime: "Centerschliessung",
          assignedEmployee: "Mitarbeiter",
          date: "Datum",
        }[field] || "Änderung"
      )
    },
    formatShiftChangeValue(value, field = "") {
      if (value === undefined || value === null || value === "") return "—"
      if (typeof value === "boolean") return value ? "Ja" : "Nein"

      if (Array.isArray(value)) {
        if (!value.length) return "Keine Zeitblöcke"
        return value
          .map((block) => {
            if (!block || typeof block !== "object") return String(block)
            const type = block.type || "Arbeitszeit"
            const time = block.start || block.end ? `${block.start || "?"}–${block.end || "?"}` : "ohne Zeit"
            return `${type} ${time}`
          })
          .join(" · ")
      }

      if (typeof value === "object") {
        if (field === "times" && Array.isArray(value.times)) return this.formatShiftChangeValue(value.times, field)
        if (value.fullName || value.name) return value.fullName || value.name
        if (value.start || value.end) return `${value.start || "?"}–${value.end || "?"}`
        return (
          Object.values(value)
            .filter((entry) => entry !== undefined && entry !== null && typeof entry !== "object")
            .join(" · ") || "—"
        )
      }

      return String(value)
    },
    async confirmPendingShiftChanges() {
      if (!this.requiresShiftChangeReview) return
      const historyIds = this.activePendingShiftChanges.map((entry) => this.shiftChangeId(entry)).filter(Boolean)
      if (!historyIds.length) return
      if (!confirm(`${historyIds.length} Schichtänderung${historyIds.length === 1 ? "" : "en"} als geprüft bestätigen?`)) return

      this.shiftChangeReviewLoading = true
      try {
        await api.bulkReviewShiftChangeHistory({
          historyIds,
          status: "approved",
          reviewNote: this.shiftChangeReviewNote.trim(),
        })
        const reviewedAt = new Date().toISOString()
        const reviewNote = this.shiftChangeReviewNote.trim()
        historyIds.forEach((historyId) => {
          this.$set(this.localShiftReviewMeta, historyId, { reviewedAt, reviewNote })
        })
        this.locallyReviewedShiftChangeIds = Array.from(new Set([...this.locallyReviewedShiftChangeIds, ...historyIds]))
        this.shiftChangeReviewNote = ""
        this.$emit("shift-changes-reviewed", historyIds)
        this.$toast.success(`${historyIds.length} Schichtänderung${historyIds.length === 1 ? "" : "en"} als geprüft bestätigt.`)
      } catch (error) {
        const message = error?.response?.data?.message || "Die Schichtänderungen konnten nicht bestätigt werden."
        this.$toast.error(message)
      } finally {
        this.shiftChangeReviewLoading = false
      }
    },
    async generateAndDownloadExcel() {
      this.exportingExcel = true
      try {
        let current = this.$moment(this.exportStartMonth, "YYYY-MM").startOf("month")
        let end = this.$moment(this.exportEndMonth, "YYYY-MM").endOf("month")

        let payload = {
          employeeName: this.timeEntryView.fullName,
          dateRange: `${current.format("MM.YYYY")} - ${end.format("MM.YYYY")}`,
          months: [],
          totalSummary: [],
          allAbsenceRows: [],
        }

        let totalShould = 0
        let totalIs = 0

        while (current.isSameOrBefore(end, "month")) {
          let dateStr = current.format("YYYY-MM-DD")
          let startDate = current.clone().startOf("month").format("YYYY-MM-DD")
          let endDate = current.clone().endOf("month").format("YYYY-MM-DD")
          let range = [startDate, endDate]

          let studioEmps = await api.fetchOfficeTimeEntriesForStudio(range, this.studio)
          let empData = studioEmps.find((e) => e.user === this.timeEntryView.user)

          if (!empData) {
            current.add(1, "month")
            continue
          }

          let monthObj = {
            name: current.format("MMMM YYYY"),
            targetHours: "-",
            workedHours: "-",
            difference: "-",
            attendanceRows: [],
          }

          const parseHours = (val) => {
            if (val === undefined || val === null) return 0
            if (typeof val === "number") return val
            let str = val.toString().toUpperCase().trim()
            let isNeg = str.startsWith("-")
            if (str.includes("H")) {
              let parts = str.split("H")
              let h = Math.abs(parseFloat(parts[0].replace(/[^\d.-]/g, "")) || 0)
              let m = 0
              if (parts[1] && parts[1].includes("M")) {
                m = Math.abs(parseFloat(parts[1].replace(/[^\d.-]/g, "")) || 0)
              }
              let total = h + m / 60
              return isNeg ? -total : total
            }
            return Number(str.replace(/,/g, ".").replace(/[^\d.-]/g, "")) || 0
          }

          let getPensumForMonth = (employee, dateObj) => {
            if (employee.noFixPercentage) return 0
            let targetFormat = dateObj.format("YYYY-MM")
            let studios = employee.allStudios || employee.studio || []
            let studioEntry = studios.find((s) => s.mainStudio)
            if (!studioEntry && studios.length) {
              studioEntry = studios[0]
            }
            let pensumHistory = (studioEntry && studioEntry.pensumHistory) || []
            if (!pensumHistory.length) {
              let withHistory = studios.find((s) => s.pensumHistory && s.pensumHistory.length > 0)
              if (withHistory) {
                pensumHistory = withHistory.pensumHistory
              }
            }
            if (!pensumHistory.length) return employee.percentage || 100
            let sortedHistory = pensumHistory.slice().sort((a, b) => b.validFrom.localeCompare(a.validFrom))
            let applicable = sortedHistory.find((h) => h.validFrom <= targetFormat)
            return applicable ? applicable.percentage : employee.percentage || 100
          }

          let ttm = empData.timeTrackerMonths ? empData.timeTrackerMonths.find((t) => t.month === current.format("YYYY-MM")) : null
          if (ttm && (ttm.state === "accepted" || ttm.state === "submitted")) {
            let durationParsed = parseHours(ttm.durationInHours)
            let diffParsed = parseHours(ttm.difference)
            let targetParsed = durationParsed - diffParsed

            if (ttm.durationInHours === undefined && ttm.difference === undefined) {
              // Dynamic retroactive computation (DB stripped data previously)
              let currentMonthPercentage = getPensumForMonth(empData, current)
              let businessDays = 0
              let mDate = current.clone().startOf("month")
              let entryDate = empData.entryDate ? this.$moment(empData.entryDate) : null
              let exitDate = empData.exitDate ? this.$moment(empData.exitDate) : null

              while (mDate.isSameOrBefore(current.clone().endOf("month"), "day")) {
                let isAfterEntry = !entryDate || mDate.isSameOrAfter(entryDate, "day")
                let isBeforeExit = !exitDate || mDate.isSameOrBefore(exitDate, "day")
                if (mDate.day() !== 0 && mDate.day() !== 6 && isAfterEntry && isBeforeExit) {
                  businessDays++
                }
                mDate.add(1, "day")
              }

              let studioId = this.studio && this.studio._id ? this.studio._id : this.studio
              let holidayCode = this.resolveHolidayCode(studioId, empData)
              let holidayDates = new Set()

              if (this.holidaysData && this.holidaysData.length) {
                this.holidaysData.forEach((h) => {
                  let start = h.startDate && h.startDate.$date ? h.startDate.$date : h.startDate
                  if (!start) return
                  let hDate = this.$moment(start, "YYYY-MM-DD")
                  if (hDate.format("YYYY-MM") !== current.format("YYYY-MM")) return

                  if (entryDate && hDate.isBefore(entryDate, "day")) return
                  if (exitDate && hDate.isAfter(exitDate, "day")) return
                  if (hDate.day() === 0 || hDate.day() === 6) return

                  let isMatch = h.nationwide || (holidayCode && h.type === "Public" && h.cantons && h.cantons.some((c) => holidayCode.startsWith(c)))
                  if (isMatch) {
                    holidayDates.add(hDate.format("YYYY-MM-DD"))
                  }
                })
              }

              // Add custom holidays and deduplicate
              if (studioId && this.storeStudios && this.storeStudios.length > 0) {
                let s = this.storeStudios.find((std) => std._id === studioId.toString())
                if ((!s || !s.vacationDays || s.vacationDays.length === 0) && empData && empData.studio) {
                  let teamEntry = empData.studio.find((studl) => studl.team && studl.team.toString() === studioId.toString())
                  if (teamEntry && teamEntry._id) {
                    s = this.storeStudios.find((std) => std._id === teamEntry._id.toString())
                  }
                }
                if (s && s.vacationDays) {
                  s.vacationDays.forEach((d) => {
                    let hDate = this.$moment(d.date, "YYYY-MM-DD")
                    if (hDate.format("YYYY-MM") !== current.format("YYYY-MM")) return
                    if (entryDate && hDate.isBefore(entryDate, "day")) return
                    if (exitDate && hDate.isAfter(exitDate, "day")) return
                    if (hDate.day() === 0 || hDate.day() === 6) return
                    holidayDates.add(hDate.format("YYYY-MM-DD"))
                  })
                }
              }
              let holidayCount = holidayDates.size

              targetParsed = Math.round((businessDays - holidayCount) * ((8.4 * currentMonthPercentage) / 100) * 1000) / 1000

              let totalWorkedMins = 0
              if (empData.attendance) {
                Object.keys(empData.attendance)
                  .filter((d) => d.startsWith(current.format("YYYY-MM")))
                  .forEach((dateStr) => {
                    let timeEntries = empData.attendance[dateStr] || []
                    let inWork = false
                    let lastWorkStart = null

                    let sickMins = 0
                    timeEntries
                      .filter((e) => e.type === "sick")
                      .forEach((e) => {
                        if (e.hours) sickMins += e.hours * 60
                      })
                    totalWorkedMins += sickMins

                    let holEntry = timeEntries.find((e) => e.type === "holiday")
                    if (holEntry && holEntry.hours) totalWorkedMins += holEntry.hours * 60

                    timeEntries.forEach((entry, index) => {
                      if (entry.correction === "open" && !entry.beforeCorrectionTime) return
                      if (entry.type === "checkout" || entry.type === "pause") {
                        if (inWork) {
                          inWork = false
                          totalWorkedMins += this.$moment(dateStr + " " + entry.time).diff(lastWorkStart, "minutes")
                        } else if (index === 0) {
                          inWork = true
                          lastWorkStart = this.$moment(dateStr).startOf("day")
                          totalWorkedMins += this.$moment(dateStr + " " + entry.time).diff(lastWorkStart, "minutes")
                        }
                      } else if (entry.type === "checkin" || entry.type === "pauseend") {
                        if (!inWork) {
                          inWork = true
                          lastWorkStart = this.$moment(dateStr + " " + entry.time)
                        }
                      }

                      if (index === timeEntries.length - 1 && (entry.type === "checkin" || entry.type === "pauseend")) {
                        let startOfDay = this.$moment(dateStr).startOf("day")
                        let isToday = startOfDay.isSame(this.$moment().startOf("day"), "day")
                        if (isToday) {
                          totalWorkedMins += this.$moment().diff(lastWorkStart, "minutes")
                        } else {
                          totalWorkedMins += this.$moment(startOfDay).endOf("day").diff(lastWorkStart, "minutes")
                        }
                      }
                    })
                  })
              }
              durationParsed = totalWorkedMins / 60
              diffParsed = durationParsed - targetParsed

              monthObj.targetHours = targetParsed.toFixed(2) + " h"
              monthObj.workedHours = durationParsed.toFixed(2) + " h"
              monthObj.difference = diffParsed.toFixed(2) + " h"

              if (entryDate && entryDate.isAfter(current.clone().startOf("month"))) {
                monthObj.partialNote = `Reduziert (Eintritt am ${entryDate.format("DD.MM.YYYY")})`
              } else if (exitDate && exitDate.isBefore(current.clone().endOf("month"))) {
                monthObj.partialNote = `Reduziert (Austritt am ${exitDate.format("DD.MM.YYYY")})`
              }
            } else {
              monthObj.targetHours = targetParsed.toFixed(2) + " h"
              monthObj.workedHours = durationParsed.toFixed(2) + " h"
              monthObj.difference = diffParsed.toFixed(2) + " h"
            }
            totalShould += targetParsed
            totalIs += durationParsed
          } else {
            let currentMonthPercentage = getPensumForMonth(empData, current)
            let businessDays = 0
            let day = current.clone().startOf("month")
            let entryDate = empData.entryDate ? this.$moment(empData.entryDate) : null
            let exitDate = empData.exitDate ? this.$moment(empData.exitDate) : null
            while (day.isSameOrBefore(current.clone().endOf("month"), "day")) {
              let isAfterEntry = !entryDate || day.isSameOrAfter(entryDate, "day")
              let isBeforeExit = !exitDate || day.isSameOrBefore(exitDate, "day")
              if (day.day() !== 0 && day.day() !== 6 && isAfterEntry && isBeforeExit) {
                businessDays++
              }
              day.add(1, "day")
            }
            let approxTarget = Math.round(businessDays * ((8.4 * currentMonthPercentage) / 100) * 1000) / 1000

            monthObj.workedHours = "Nicht abgeschlossen"
            monthObj.targetHours = approxTarget.toFixed(2) + " h (Richtwert ohne Feiertage)"
            totalShould += approxTarget
          }

          if (empData.attendance) {
            let sortedDates = Object.keys(empData.attendance).sort()
            for (let d of sortedDates) {
              let timesStr = ""
              let firstType = "Anwesenheit"
              let lastNote = ""

              for (let att of empData.attendance[d]) {
                if (att.correction === "Versteckt" || att.delete || att.disableCorrection) continue
                let actionText = this.mappedEntryTypes[att.type] || att.type
                if (att.absenceReason) actionText = this.mappedEntryTypes[att.absenceReason] || att.absenceReason
                let noteText = att.note || ""
                if (noteText && !lastNote.includes(noteText)) lastNote += noteText + " "

                if (att.type === "checkin" || att.type === "pauseend") {
                  timesStr += att.time + " - "
                } else if (att.type === "checkout" || att.type === "pause") {
                  timesStr += att.time + " | "
                } else {
                  timesStr += `[${actionText} ${att.time || ""}] `
                  if (firstType === "Anwesenheit") firstType = actionText
                }

                if (
                  att.type === "sick" ||
                  att.type === "holiday" ||
                  actionText === "Andere" ||
                  actionText === "Militär" ||
                  actionText === "Schule" ||
                  actionText === "Schwangerschaft"
                ) {
                  payload.allAbsenceRows.push([current.format("MMMM YYYY"), this.$moment(d).format("DD.MM.YYYY"), actionText, noteText])
                }
              }

              if (timesStr) {
                timesStr = timesStr.replace(/(- |\| )$/, "").trim()
                monthObj.attendanceRows.push([
                  this.$moment(d).format("dddd"),
                  this.$moment(d).format("DD.MM.YYYY"),
                  firstType,
                  timesStr,
                  lastNote.trim(),
                ])
              }
            }
          }
          payload.months.push(monthObj)
          current.add(1, "month")
        }

        payload.totalSummary.push(["Total Sollstunden:", totalShould.toFixed(2) + " h", "Erfordert abgeschlossene/eingereichte Monate"])
        payload.totalSummary.push(["Total Iststunden:", totalIs.toFixed(2) + " h", "Erfordert abgeschlossene/eingereichte Monate"])
        payload.totalSummary.push(["Total Differenz:", (totalIs - totalShould).toFixed(2) + " h", "Ist minus Soll"])

        // Trigger export
        import("file-saver").then(async ({ saveAs }) => {
          let blob = await api.exportEmployeeExcel(payload)
          saveAs(blob, `Abrechnung_${this.timeEntryView.fullName}_${this.exportStartMonth}_bis_${this.exportEndMonth}.xlsx`)
          this.exportExcelDialog = false
        })
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Exportieren")
      } finally {
        this.exportingExcel = false
      }
    },
    async exportToExcel() {
      this.$toast.info("Excel wird erstellt...")
      const data = []
      const attendance = this.timeEntryView.attendance

      // Kopfzeile
      data.push(["Datum", "Firma", "Typ", "Uhrzeit", "Kommentar"])

      const sortedDays = Object.keys(attendance).sort()

      sortedDays.forEach((day) => {
        // FILTER: Badge-Einträge (technicCheckin/technicCheckout) ausschliessen
        const relevantEntries = attendance[day].filter((entry) => entry.type !== "technicCheckin" && entry.type !== "technicCheckout")

        relevantEntries.forEach((entry) => {
          data.push([
            this.$moment(day).format("DD.MM.YYYY"),
            entry.company || "Firma 1",
            this.mappedEntryTypes[entry.type] || entry.type,
            entry.time,
            entry.reason || "",
          ])
        })
        if (relevantEntries.length > 0) data.push([])
      })

      data.push(["ZUSAMMENFASSUNG"])
      const statsF1 = this.calculateStatsForCompany("Firma 1")
      const statsF2 = this.calculateStatsForCompany("Firma 2")

      data.push(["Total Firma 1", statsF1.formattedTime + " h"])
      data.push(["Total Firma 2", statsF2.formattedTime + " h"])
      data.push(["Gesamtstunden", ((statsF1.totalMinutes + statsF2.totalMinutes) / 60).toFixed(2) + " h"])

      let csvContent = "data:text/csv;charset=utf-8," + data.map((e) => e.join(";")).join("\n")

      const encodedUri = encodeURI(csvContent)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("download", `Abrechnung_${this.timeEntryView.fullName}_${this.$moment(this.month).format("YYYY-MM")}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    calculateStatsForCompany(companyName) {
      let totalMinutes = 0
      const attendance = this.timeEntryView.attendance

      Object.keys(attendance).forEach((day) => {
        // Auch hier die Badge-Einträge für die Summenbildung ignorieren
        const dayEntries = attendance[day].filter(
          (e) => (e.company || "Firma 1") === companyName && e.type !== "technicCheckin" && e.type !== "technicCheckout",
        )

        let lastIn = null
        dayEntries.forEach((entry) => {
          if (entry.type === "checkin" || entry.type === "pauseend") {
            lastIn = this.$moment(day + " " + entry.time)
          } else if ((entry.type === "checkout" || entry.type === "pause") && lastIn) {
            totalMinutes += this.$moment(day + " " + entry.time).diff(lastIn, "minutes")
            lastIn = null
          }
        })
      })

      return {
        totalMinutes,
        formattedTime: (totalMinutes / 60).toFixed(2),
      }
    },
    async updateNote() {
      await api.submitTimeTrackerMonth({
        _id: this.timeEntryView.user,
        month: this.month,
        note: this.timeTrackerMonth.note,
        state: "onlyNotes",
      })
      this.$toast.success("Notiz wurde gespeichert")
    },
    async saveSelfCheckinEntryEdit() {
      try {
        await api.saveSelfCheckinEntryEditTODB({ item: this.selfCheckinEntryEdit, user: this.timeEntryView })
        this.selfCheckinEntryEdit = null
        this.changeCanSelfCheckinNoAdminDialog = false
        this.$toast.success("Änderung wurde gespeichert")
        this.$emit("fetchEntries")
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Die Änderung konnte nicht vollständig gespeichert werden. Bitte neu laden.")
      }
    },
    changeCanSelfCheckinNoAdmin(item, newEntry = false) {
      if (newEntry) {
        console.log(item)
        this.selfCheckinEntryEdit = {
          type: "",
          time: "",
          realDate: item.realDate,
        }
      } else {
        this.selfCheckinEntryEdit = { ...item }
        this.selfCheckinEntryEdit.preservedTime = JSON.parse(JSON.stringify(item.time))
      }
      this.changeCanSelfCheckinNoAdminDialog = true
    },
    hasCorrectionsOpenAlert() {
      alert("Es sind noch Korrekturen offen")
    },
    async fixToShortTime(item, option) {
      const originalAttendances = Array.isArray(this.timeEntryView.attendance[item.realDate]) ? this.timeEntryView.attendance[item.realDate] : []
      let attendances = originalAttendances
        // Sick/holiday entries are display-only additions from their own
        // collections. They must never participate in, or be persisted by,
        // an attendance correction.
        .filter((att) => att && !String(att.type || "").includes("error") && ["checkin", "checkout"].includes(att.oldType))
        .map((x) => {
          return {
            time: x.time,
            oldType: x.oldType,
            type: x.type,
            autoFixedTime: x.time,
            studio: x.studio,
          }
        })
      const oldAttendances = attendances.map((attendance) => ({ ...attendance }))
      let totalWorkTime = 0
      let totalBreakTime = 0
      let breakPairs = []

      for (let i = 0; i < attendances.length - 1; ) {
        if (attendances[i].oldType === "checkin" && attendances[i + 1].oldType === "checkout") {
          let checkinTime = this.$moment(attendances[i].time, "HH:mm:ss")
          let checkoutTime = this.$moment(attendances[i + 1].time, "HH:mm:ss")

          totalWorkTime += checkoutTime.diff(checkinTime, "minutes")
          i += 2

          if (i < attendances.length - 1 && attendances[i].oldType === "checkin") {
            let nextCheckinTime = this.$moment(attendances[i].time, "HH:mm:ss")
            let breakTime = nextCheckinTime.diff(checkoutTime, "minutes")
            totalBreakTime += breakTime
            breakPairs.push({ start: checkoutTime, end: nextCheckinTime, duration: breakTime })
          }
        } else {
          i++
        }
      }

      let requiredBreak = this.getRequiredBreak(totalWorkTime)
      if (attendances.length === 2 && attendances[0].oldType === "checkin" && attendances[1].oldType === "checkout") {
        const originalCheckoutTime = this.$moment(attendances[1].time, "HH:mm:ss")
        const breakMinutesBeforeCheckout = option === "extendWorktime" ? Math.floor(requiredBreak / 2) : requiredBreak
        const breakMinutesAfterCheckout = option === "extendWorktime" ? requiredBreak - breakMinutesBeforeCheckout : 0
        const breakStart = originalCheckoutTime.clone().subtract(breakMinutesBeforeCheckout, "minutes")
        const breakEnd = originalCheckoutTime.clone().add(breakMinutesAfterCheckout, "minutes")

        if (option === "extendWorktime") {
          attendances[1].time = originalCheckoutTime.clone().add(requiredBreak, "minutes").format("HH:mm:ss")
        }

        attendances.splice(
          1,
          0,
          {
            time: breakStart.format("HH:mm:ss"),
            type: "pause",
            oldType: "checkout",
          },
          {
            time: breakEnd.format("HH:mm:ss"),
            type: "pauseend",
            oldType: "checkin",
          },
        )

        breakPairs.push({
          start: breakStart,
          end: breakEnd,
          duration: requiredBreak,
        })
        totalBreakTime += requiredBreak
      }

      if (totalBreakTime < requiredBreak) {
        let diff = requiredBreak - totalBreakTime

        if (option === "extendPause") {
          if (breakPairs.length > 0) {
            let lastBreak = breakPairs[breakPairs.length - 1]
            if (lastBreak) {
              lastBreak.end.add(diff, "minutes")
              lastBreak.duration += diff
            }
          }
        } else if (option === "extendWorktime") {
          if (breakPairs.length > 0) {
            let lastBreak = breakPairs[breakPairs.length - 1]
            if (lastBreak) {
              lastBreak.end.add(diff, "minutes")
              lastBreak.duration += diff
            }

            let lastCheckout = attendances.find((att) => att.oldType === "checkout" && this.$moment(att.time, "HH:mm:ss").isAfter(lastBreak.end))
            let lastCheckoutIndex = attendances.findIndex(
              (att) => att.oldType === "checkout" && this.$moment(att.time, "HH:mm:ss").isAfter(lastBreak.end),
            )

            if (lastCheckout) {
              let lastCheckoutTime = this.$moment(lastCheckout.time, "HH:mm:ss")
              lastCheckoutTime.add(diff, "minutes")
              attendances[lastCheckoutIndex].time = lastCheckoutTime.format("HH:mm:ss")
            } else {
              let lastCheckout = attendances[attendances.length - 1]
              if (lastCheckout.oldType === "checkout") {
                let lastCheckoutTime = this.$moment(lastCheckout.time, "HH:mm:ss")
                lastCheckoutTime.add(diff, "minutes")
                lastCheckout.time = lastCheckoutTime.format("HH:mm:ss")
              }
            }
          }
        }
        for (let i = 0; i < attendances.length - 1; i++) {
          if (attendances[i].oldType === "checkout" && attendances[i + 1].oldType === "checkin") {
            let breakPair = breakPairs.shift()
            if (breakPair) {
              attendances[i].time = breakPair.start.format("HH:mm:ss")
              attendances[i + 1].time = breakPair.end.format("HH:mm:ss")
            }
          }
        }
      }
      this.preSavedAttendances = {
        new: attendances,
        old: oldAttendances,
        date: item.realDate,
        adjustmentInfo: {
          option: option,
          reason: option === "extendWorktime" ? "Einzelbesetzung" : "",
        },
      }
      this.fixTimeDialog = true
    },
    getRequiredBreak(workDuration) {
      if (workDuration <= 330) {
        return 0
      } else if (workDuration < 420) {
        return 15
      } else if (workDuration < 540) {
        return 30
      } else {
        return 60
      }
    },
    async savePreSavedAttendances() {
      try {
        await api.savePreSavedAttendancesToDB(this.preSavedAttendances, this.timeEntryView.user)
      } catch (error) {
        this.$toast.error("Die Pausenkorrektur konnte nicht gespeichert werden.")
        return
      }

      if (this.reloadAfterChanges) {
        // Reload so separately sourced sickness/holiday entries are merged
        // back into the view exactly once.
        this.$emit("fetchEntries")
      } else {
        const date = this.preSavedAttendances.date
        const displayOnlyEntries = (this.timeEntryView.attendance[date] || []).filter(
          (attendance) => !["checkin", "checkout"].includes(attendance.oldType),
        )
        this.$set(
          this.timeEntryView.attendance,
          date,
          [...this.preSavedAttendances.new, ...displayOnlyEntries].sort((a, b) => String(a.time || "").localeCompare(String(b.time || ""))),
        )
        this.$toast.warning("Neuladen um Anzeigefehler zu vermeiden.")
      }

      this.fixTimeDialog = false
      this.preSavedAttendances = null
      this.fixTimeDialogOptionPicker = false
      this.soloShiftConfirmDialog = false
      this.soloShiftConfirmed = false
      this.selectedOption = null
    },
    selectOption(option) {
      this.selectedOption = option
      this.fixTimeDialogOptionPicker = false
      if (option === "extendWorktime") {
        this.soloShiftConfirmed = false
        this.soloShiftConfirmDialog = true
      } else {
        this.fixToShortTime(this.fixTimeDialogItem, "extendPause")
      }
    },
    confirmSoloShift() {
      this.soloShiftConfirmDialog = false
      this.fixToShortTime(this.fixTimeDialogItem, "extendWorktime")
    },
    cancelSoloShiftConfirm() {
      this.soloShiftConfirmDialog = false
      this.fixTimeDialogOptionPicker = true
    },
    cancelFixTimeDialog() {
      this.fixTimeDialog = false
      this.preSavedAttendances = null
    },
    getAdjustmentInfo(realDate) {
      if (!this.timeEntryView || !this.timeEntryView.attendance) return null
      const entries = this.timeEntryView.attendance[realDate]
      if (!entries || !Array.isArray(entries)) return null
      const entryWithAdjustment = entries.find((entry) => entry.adjustmentInfo)
      return entryWithAdjustment ? entryWithAdjustment.adjustmentInfo : null
    },
    getPauseChipColor(item) {
      if (!this.checkToShortPause(item)) {
        return "red"
      }
      if (this.getAdjustmentInfo(item.realDate)) {
        return "blue-grey darken-2"
      }
      return "grey"
    },
    async deleteSickEntry(item) {
      if (confirm("Willst du den Eintrag wirklich löschen?")) {
        await api.deleteSickEntry({ ...item, employee: this.timeEntryView.user })
        if (this.reloadAfterChanges) {
          this.$emit("fetchEntries")
        } else {
          this.$toast.warning("Neuladen um Anzeigefehler zu vermeiden.")
        }
      }
    },
    submitTimeTrackerMonth(state = "submitted") {
      let overTime = this.timeEntryView.overTime || 0

      // Ablehnen muss auch bei unzureichenden Pausen möglich sein.
      if (state !== "rejected") {
        let validation = true
        let tableItem = null
        this.tableItems.forEach((item) => {
          if (validation) {
            validation = this.checkToShortPause(item)
            if (!validation) tableItem = item
          }
        })
        if (!validation) return alert("Bitte prüfe die Pausenzeit vom " + this.$moment(tableItem.realDate, "YYYY-MM-DD").format("DD. MMMM ") + "")
      }

      // Berechnungen für den Dialog
      let difference = 0
      let formattedDuration = this.getTotalDuration.formattedTime

      if (this.currentMonthPercentage && !this.timeEntryView.noFixPercentage) {
        difference =
          Math.round(
            (formattedDuration -
              (this.calcBusinessDays - this.vacationDaysForMonth(this.studio).length) * ((8.4 * this.currentMonthPercentage) / 100)) *
              1000,
          ) / 1000
      }

      // Daten in State speichern
      this.submitOfficeData = {
        duration: formattedDuration,
        difference: difference,
        percentage: this.timeEntryView.noFixPercentage ? 0 : this.currentMonthPercentage,
        overtimeBefore: overTime,
        newOvertime: this.timeEntryView.noFixPercentage ? overTime : Math.round((overTime + difference) * 100) / 100,
        targetState: state,
      }

      // Wenn es ein einfacher Submit (z.B. Mitarbeiter selbst) ist ohne spezielle Berechnungen,
      // oder wenn es nur um Notizen geht, nutzen wir den alten Weg (optional, oder immer Dialog).
      // Hier triggern wir den neuen Dialog für den Vorgesetzten-Kontext:
      this.showSubmitOfficeDialog = true
    },

    // 2. Die eigentliche Ausführung nach Klick im Dialog
    async confirmOfficeSubmission() {
      if (!this.office && this.requiresShiftChangeReview && !this.shiftChangeHistoryLoaded) {
        this.$toast.error(this.shiftChangeHistoryError || "Der Prüfstatus der Schichtänderungen ist noch nicht verfügbar.")
        return
      }
      if (!this.office && this.requiresShiftChangeReview && this.pendingShiftChangeReviewCount > 0) {
        this.$toast.error(
          this.unresolvedPendingShiftChangeCount > 0
            ? "Mindestens eine offene Schichtänderung konnte nicht zugeordnet werden. Bitte die Übersicht neu laden."
            : "Bitte zuerst alle offenen Schichtänderungen prüfen.",
        )
        return
      }
      this.submitLoading = true
      try {
        let state = this.submitOfficeData.targetState

        // Submission State Objekt vorbereiten (falls "accepted")
        if (state === "accepted") {
          this.submissionState.overTimeBefore = this.submitOfficeData.overtimeBefore
          this.submissionState.overTimeAfter = this.submitOfficeData.newOvertime
          this.submissionState.percentage = this.submitOfficeData.percentage
          this.submissionState.durationInHours = this.submitOfficeData.duration
          this.submissionState.difference = this.submitOfficeData.difference
        }

        // API Call
        if (this.submissionState) {
          await api.updateTimeTrackerMonth({
            _id: this.timeEntryView.user,
            submissionState: this.submissionState,
            state,
          })
        } else {
          await api.submitTimeTrackerMonth({
            _id: this.timeEntryView.user,
            month: this.month,
            note: this.timeTrackerMonth.note,
          })
        }

        // Cleanup
        this.showSubmitOfficeDialog = false

        if (this.reloadAfterChanges) {
          this.$emit("fetchEntries")
        } else {
          this.$toast.warning("Neuladen um Anzeigefehler zu vermeiden.")
        }
        this.$toast.success("Erfolgreich gespeichert")
      } catch (e) {
        console.error(e)
        const message = e?.response?.data?.message || "Fehler beim Speichern"
        this.$toast.error(message)
        if (e?.response?.data?.code === "SHIFT_CHANGE_REVIEW_PENDING") {
          this.$emit("fetchEntries")
        }
      } finally {
        this.submitLoading = false
      }
    },
    parseHoursValue(val) {
      if (val === null || val === undefined) return 0
      if (typeof val === "number") return val
      // Handle string with comma decimal separator (e.g. "8,4" -> 8.4)
      let parsed = parseFloat(String(val).replace(",", "."))
      return isNaN(parsed) ? 0 : parsed
    },
    getAbsenceDuration(attendanceItem) {
      let totalDuration = 0
      let notes = []

      const absenceTypes = ["sick", "holiday", "military", "school", "pregnancy", "other"]

      attendanceItem.forEach((entry) => {
        if (absenceTypes.includes(entry.type) || absenceTypes.includes(entry.absenceReason)) {
          totalDuration += this.parseHoursValue(entry.hours) * 60
          if (entry.note) notes.push(entry.note)
        }
      })

      return {
        formattedTime: totalDuration > 0 ? this.$moment.utc(totalDuration * 60 * 1000).format("HH[H] mm[M]") : "-",
        note: notes.join(", "),
      }
    },
    resolveHolidayCode(studioOrTeamId, employeeData = null) {
      let emp = employeeData || this.timeEntryView
      if (emp && emp.resolvedStudioId) {
        studioOrTeamId = emp.resolvedStudioId
      }

      // If studioOrTeamId is invalid, try to get the studio from employee data
      if (!studioOrTeamId || studioOrTeamId === "undefined" || studioOrTeamId === "null" || studioOrTeamId === "all") {
        if (emp) {
          let studios = emp.allStudios || emp.studio || []
          let mainStudio = studios.find((s) => s.mainStudio) || studios[0]
          if (mainStudio && mainStudio._id) {
            studioOrTeamId = mainStudio._id.toString()
          }
        }
      }

      if (!studioOrTeamId || studioOrTeamId === "undefined" || studioOrTeamId === "null" || studioOrTeamId === "all") {
        return "TG-FF-FF"
      }

      const targetIdStr = studioOrTeamId.toString()

      // 1. Try direct studio lookup
      let fullStudio = this.storeStudios.find((std) => std._id && std._id.toString() === targetIdStr)
      if (fullStudio && fullStudio.holidayCode) return fullStudio.holidayCode

      // 2. If not found, the ID is likely a team — find the linked studio via employee data
      let studioArr = emp ? emp.allStudios || emp.studio || [] : []

      let teamEntry = studioArr.find((s) => {
        if (!s.team) return false
        const sTeamId = s.team._id ? s.team._id.toString() : s.team.toString()
        return sTeamId === targetIdStr
      })

      if (teamEntry && teamEntry._id) {
        let linkedStudio = this.storeStudios.find((std) => std._id && std._id.toString() === teamEntry._id.toString())
        if (linkedStudio && linkedStudio.holidayCode) return linkedStudio.holidayCode
      }

      // 3. Fallback
      return "TG-FF-FF"
    },
    vacationDaysForMonth(studio) {
      if (studio && studio._id) studio = studio._id
      let emp = this.timeEntryView
      if (emp && emp.resolvedStudioId) {
        studio = emp.resolvedStudioId
      }
      let holidayCode = this.resolveHolidayCode(studio)
      let monthFormat = this.$moment(this.month).format("YYYY-MM")

      let matchedHolidays = this.holidaysData.filter((holiday) => {
        let start = holiday.startDate && holiday.startDate.$date ? holiday.startDate.$date : holiday.startDate
        if (!start) return false

        if (this.$moment(start).format("YYYY-MM") !== monthFormat) return false

        let holidayDate = this.$moment(start, "YYYY-MM-DD")

        if (holidayDate.isoWeekday() === 6 || holidayDate.isoWeekday() === 7) return false

        let entryDate = this.timeEntryView.entryDate ? this.$moment(this.timeEntryView.entryDate, "YYYY-MM-DD") : null
        let exitDate = this.timeEntryView.exitDate ? this.$moment(this.timeEntryView.exitDate, "YYYY-MM-DD") : null

        if (entryDate && holidayDate.isBefore(entryDate, "day")) return false
        if (exitDate && holidayDate.isAfter(exitDate, "day")) return false

        if (holiday.nationwide) return true
        if (holidayCode && holiday.type === "Public" && holiday.cantons && holiday.cantons.some((c) => holidayCode.startsWith(c))) {
          return true
        }
        return false
      })

      // Add custom studio/team vacation days (feiertage)
      let customVacationDays = []
      if (studio && this.storeStudios && this.storeStudios.length > 0) {
        let s = this.storeStudios.find((std) => std._id === studio.toString())
        if ((!s || !s.vacationDays || s.vacationDays.length === 0) && this.timeEntryView && this.timeEntryView.studio) {
          let teamEntry = this.timeEntryView.studio.find((studl) => studl.team && studl.team.toString() === studio.toString())
          if (teamEntry && teamEntry._id) {
            s = this.storeStudios.find((std) => std._id === teamEntry._id.toString())
          }
        }
        if (s && s.vacationDays) {
          let entryDate = this.timeEntryView.entryDate ? this.$moment(this.timeEntryView.entryDate, "YYYY-MM-DD") : null
          let exitDate = this.timeEntryView.exitDate ? this.$moment(this.timeEntryView.exitDate, "YYYY-MM-DD") : null

          customVacationDays = s.vacationDays
            .filter((d) => {
              let hDate = this.$moment(d.date, "YYYY-MM-DD")
              if (hDate.format("YYYY-MM") !== monthFormat) return false
              if (entryDate && hDate.isBefore(entryDate, "day")) return false
              if (exitDate && hDate.isAfter(exitDate, "day")) return false
              if (hDate.isoWeekday() === 6 || hDate.isoWeekday() === 7) return false
              return true
            })
            .map((d) => ({
              title: d.title,
              date: d.date,
            }))
        }
      }

      const allDays = [
        ...matchedHolidays.map((hol) => ({
          title: hol.name,
          date: hol.startDate && hol.startDate.$date ? hol.startDate.$date : hol.startDate,
        })),
        ...customVacationDays,
      ]

      // Deduplicate by date
      const uniqueDays = []
      const seenDates = new Set()
      for (const day of allDays) {
        const formattedDate = this.$moment(day.date).format("YYYY-MM-DD")
        if (!seenDates.has(formattedDate)) {
          seenDates.add(formattedDate)
          uniqueDays.push(day)
        }
      }
      return uniqueDays
    },
    async acceptCorrection(correctedItem) {
      correctedItem.user = this.timeEntryView.user
      let correction = await api.acceptAttendanceCorrection(correctedItem)
      if (this.reloadAfterChanges) {
        this.$emit("fetchEntries")
      } else {
        this.$toast.warning("Neuladen um Anzeigefehler zu vermeiden.")
      }
    },
    async rejectCorrection(correctedItem) {
      correctedItem.user = this.timeEntryView.user
      let correction = await api.rejectAttendanceCorrection(correctedItem)
      if (this.reloadAfterChanges) {
        this.$emit("fetchEntries")
      } else {
        this.$toast.warning("Neuladen um Anzeigefehler zu vermeiden.")
      }
    },
    checkToShortPause(item) {
      let workedMinutes = this.getWorkDuration(item.realDate, item.disableCorrection, true).minutes
      let pauseDuration = this.getPauseDuration(item.realDate, item.disableCorrection).minutes
      if (workedMinutes > 330 && workedMinutes < 470) {
        return pauseDuration >= 15
      } else if (workedMinutes >= 470 && workedMinutes < 540) {
        return pauseDuration >= 30
      } else if (workedMinutes >= 540) {
        return pauseDuration >= 60
      } else {
        return true
      }
    },
    updateIt() {
      this.keyey++
    },
    showEmployeeCorretionDialog(item) {
      this.showCorrectionDialog = true
      this.correctedItem = item
    },
    tableItemsForItem(realDate, disableCorrection = false) {
      return this.timeEntryView.attendance[realDate].map((item) => {
        let correctionReason = "-"
        if (item.correction) {
          if (item.beforeCorrectionTime) {
            correctionReason = "Bearbeitet"
          } else if (item.delete) {
            correctionReason = "Gelöscht"
          } else {
            correctionReason = "Neu"
          }
          if (item.deleted) {
            correctionReason = "Gelöscht"
          }
          if (disableCorrection) {
            correctionReason = "Versteckt"
          }

          if (item.correction === "approved") {
            correctionReason = "Akzeptiert"
          }

          if (item.correction === "rejected") {
            correctionReason = "Abgelehnt"
          }
        }
        return {
          absenceReason: item.absenceReason,
          _id: item._id,
          company: item.company,
          generatedCompanySplit: item.generatedCompanySplit,
          sickEntryID: item.sickEntryID,
          date: realDate,
          correctedTo: item.correctedTo,
          beforeCorrectionTime: item.beforeCorrectionTime,
          beforeCorrectedTime: item.beforeCorrectedTime,
          beforeCorrectedType: item.beforeCorrectedType,
          beforeCorrectionType: item.beforeCorrectionType,
          delete: item.delete,
          time:
            item.time === "23:59:59"
              ? "-"
              : disableCorrection && item.correction && item.correction === "open"
              ? item.beforeCorrectionTime || "-"
              : item.time,
          type: disableCorrection ? item.beforeCorrectionType || item.correctedType || item.type : item.correctedType || item.type,
          studio:
            item.studio && this.storeStudios.find((studio) => studio._id === item.studio)
              ? this.storeStudios.find((studio) => studio._id === item.studio).name
              : "-",
          status: item.status ? this.mapHolidayStatus[item.status] : "-",
          correction: correctionReason,
          duration: item.hours ? item.hours + " Stunden" : "-",
          note: item.generatedCompanySplit ? "Automatisch berechneter Firmenwechsel" : item.note,
        }
      })
    },
    /*  async deleteSickEntry(id) {
            if (confirm('Krankheitseintrag wirklich löschen?')) {
                try {
                    let status = await api.deleteSickEntryManagement(id)
                    this.$emit('fetchEntries')
                    this.$toast.success('Krankheitseintrag gelöscht')
                } catch (e) {
                    this.$toast.error('Es gab einen Fehler beim löschen des Krankheitseintrags')
                }
            }
        }, */
    async changeHolidayStatus(holidayEntry, status) {
      if (status) {
        try {
          let status = await api.changeHolidayStatus({ _id: holidayEntry._id, status: "accepted" })
          this.$toast.success("Status aktualisiert")
          holidayEntry.status = "accepted"
        } catch (e) {
          this.$toast.error("Es gab einen Fehler beim aktualisieren des Status")
        }
      } else {
        try {
          let status = await api.changeHolidayStatus({ _id: holidayEntry._id, status: "denied" })
          this.$toast.success("Status aktualisiert")
          holidayEntry.status = "denied"
        } catch (e) {
          this.$toast.error("Es gab einen Fehler beim aktualisieren des Status")
        }
      }
    },
    shorten(reason) {
      if (reason && reason.length > 5) {
        return reason.slice(0, 5) + "..."
      } else {
        return "-"
      }
    },
    timeTrackerInfosUpdated(data) {
      if (this.reloadAfterChanges) {
        this.$emit("fetchEntries", data)
      } else {
        this.$toast.warning("Neuladen um Anzeigefehler zu vermeiden.")
      }
    },
    getStartTime(date, disableCorrection) {
      let entries = this.filteredAttendance[date] || []
      let firstWorkEvent = entries.find((e) => e.type === "checkin")
      if (date === "2023-06-16") console.log(firstWorkEvent, firstWorkEvent.correction === "open")
      if (firstWorkEvent)
        return disableCorrection && firstWorkEvent.correction && firstWorkEvent.correction === "open"
          ? firstWorkEvent.beforeCorrectionTime || "-"
          : firstWorkEvent.time
      else return "-"
    },
    getEndTime(date, disableCorrection) {
      let entries = this.filteredAttendance[date] || []
      let lastWorkEvent = entries
        .slice()
        .reverse()
        .find((e) => e.type === "checkout")
      if (lastWorkEvent)
        return disableCorrection && lastWorkEvent.correction && lastWorkEvent.correction === "open"
          ? lastWorkEvent.beforeCorrectionTime || "-"
          : lastWorkEvent.time
      else return "-"
    },
    getHolidayDuration(timeEntries) {
      if (!timeEntries.length)
        return {
          formattedTime: "-",
          minutes: 0,
        }
      let holidayEntry = timeEntries.find((e) => e.type === "holiday")
      if (!holidayEntry)
        return {
          formattedTime: "-",
          minutes: 0,
        }
      let hrs = this.parseHoursValue(holidayEntry.hours)
      return {
        formattedTime: this.$moment.utc(hrs * 60 * 60 * 1000).format("HH[H] mm[M]"),
        minutes: hrs * 60,
      }
    },
    getSickDuration(timeEntries) {
      if (!timeEntries.length)
        return {
          formattedTime: "-",
          minutes: 0,
        }

      // Count all absence types that should count as work time
      // Note: absenceReason can be "military", "school", etc. while type is "sick"
      const absenceTypes = ["sick", "military", "school", "pregnancy", "other"]
      let filteredEntries = timeEntries.filter((e) => absenceTypes.includes(e.type) || absenceTypes.includes(e.absenceReason))
      if (!filteredEntries.length)
        return {
          formattedTime: "-",
          minutes: 0,
        }

      let countMinutes = 0

      filteredEntries.forEach((entry) => {
        if (entry.hours) {
          countMinutes += this.parseHoursValue(entry.hours) * 60
        }
      })

      return {
        formattedTime: this.$moment.utc(countMinutes * 60 * 1000).format("HH[H] mm[M]"),
        minutes: countMinutes,
      }
    },
    getPauseDuration(date, disableCorrection) {
      let timeEntries = this.filteredAttendance[date] || []

      let totalPauseTime = 0
      let inPause = false
      let lastPauseStart = null
      let startetPauseFirstTime = false

      timeEntries.forEach((entry, index) => {
        if (entry.type === "pause") entry.oldType = "checkout"
        if (entry.type === "pauseend") entry.oldType = "checkin"

        if (disableCorrection && entry.correction && entry.correction === "open" && !entry.beforeCorrectionTime) {
          return
        }

        if (entry.type.includes("error") || (entry.correction === "rejected" && entry.correction.newEntry)) {
          return
        }
        if (entry.oldType === "checkout" && !inPause) {
          startetPauseFirstTime = true
          inPause = true
          lastPauseStart = entry.time
        } else if (inPause && entry.oldType === "checkin" && startetPauseFirstTime) {
          inPause = false
          totalPauseTime += this.$moment(entry.time, "HH:mm:ss").diff(this.$moment(lastPauseStart, "HH:mm:ss"), "minutes")
        }
      })
      let hours = Math.floor(totalPauseTime / 60) // Stunden berechnen
      let minutes = totalPauseTime % 60 // Minuten berechnen
      let formattedTime = hours !== 0 || minutes !== 0 ? hours.toString().padStart(2, "0") + "H " + minutes.toString().padStart(2, "0") + "M" : "-"
      return { formattedTime, totalPauseTime, minutes: minutes + hours * 60 }
    },

    getWorkDuration(date, disableCorrection, ignoreSickDuration = false) {
      let timeEntries = this.filteredAttendance[date] || []
      let inWork = false
      let lastWorkStart = null
      let totalWorkTime = 0
      if (!ignoreSickDuration) {
        totalWorkTime = this.getSickDuration(timeEntries).minutes || 0
        totalWorkTime += this.getHolidayDuration(timeEntries).minutes || 0
      }
      timeEntries.forEach((entry, index) => {
        if (disableCorrection && entry.correction && entry.correction === "open" && !entry.beforeCorrectionTime) return
        if (entry.type === "checkout" || entry.type === "pause") {
          if (inWork) {
            inWork = false
            totalWorkTime += this.$moment(
              date + " " + (disableCorrection && entry.correction && entry.correction === "open" ? entry.beforeCorrectionTime || "" : entry.time),
            ).diff(lastWorkStart, "minutes")
          } else if (index === 0) {
            // assume a pause starting at 00:00 for first entry of type "pauseend"
            inWork = true
            lastWorkStart = this.$moment(date).startOf("day")
            totalWorkTime += this.$moment(
              date +
                " " +
                (disableCorrection && entry.correction && entry.correction !== "appproved" ? entry.beforeCorrectionTime || "" : entry.time),
            ).diff(lastWorkStart, "minutes")
          }
        } else if (entry.type === "checkin" || entry.type === "pauseend") {
          if (!inWork) {
            inWork = true
            lastWorkStart = this.$moment(
              date + " " + (disableCorrection && entry.correction && entry.correction === "open" ? entry.beforeCorrectionTime || "" : entry.time),
            )
          }
        }

        if (index === timeEntries.length - 1 && (entry.type === "checkin" || entry.type === "pauseend")) {
          let startOfDay = this.$moment(date).startOf("day")
          let isToday = startOfDay.isSame(this.$moment().startOf("day"), "day")

          if (isToday) {
            const time = this.$moment()
            totalWorkTime += time.diff(lastWorkStart, "minutes")
          } else {
            const endOfDay = this.$moment(startOfDay).endOf("day")
            totalWorkTime += endOfDay.diff(lastWorkStart, "minutes")
          }
        }
      })
      let hours = Math.floor(totalWorkTime / 60) // Stunden berechnen
      let minutes = Math.floor(totalWorkTime % 60) // Minuten berechnen

      let formattedTime = hours.toString().padStart(2, "0") + "H " + minutes.toString().padStart(2, "0") + "M"
      return { formattedTime, totalWorkTime, minutes: minutes + hours * 60 }
    },
    getDurationForDay(date, disableCorrection) {
      let timeEntries = this.timeEntryView.attendance[date]
      let totalWorkTime = this.getWorkDuration(date, disableCorrection).totalWorkTime
      let totalPauseTime = this.getPauseDuration(date, disableCorrection).totalPauseTime
      let durationTimeInMinutes = totalWorkTime + totalPauseTime
      let hours = Math.floor(durationTimeInMinutes / 60) // Stunden berechnen
      let minutes = Math.floor(durationTimeInMinutes % 60) // Minuten berechnen
      let formattedTime = hours.toString().padStart(2, "0") + "H " + minutes.toString().padStart(2, "0") + "M"
      return { formattedTime, durationTimeInMinutes }
    },
    getDuration(date, disableCorrection) {
      let timeEntries = this.timeEntryView.attendance[date]
      let totalWorkTime = this.getWorkDuration(date, disableCorrection).totalWorkTime
      let totalPauseTime = this.getPauseDuration(date, disableCorrection).totalPauseTime
      let durationTimeInMinutes = totalWorkTime - totalPauseTime
      let hours = Math.floor(durationTimeInMinutes / 60) // Stunden berechnen
      let minutes = durationTimeInMinutes % 60 // Minuten berechnen

      let formattedTime = hours.toString().padStart(2, "0") + "H " + minutes.toString().padStart(2, "0") + "M"
      return { formattedTime, durationTimeInMinutes }
    },
  },
}
</script>

<style lang="scss" scoped>
.glass-card {
  background: #1e1e1e; /* Fallback */
  background: rgba(30, 30, 30, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.icon-ring {
  border: 6px solid #1e1e1e; /* Sollte matchen mit Dialog Overlay oder Hintergrund */
}
.shadow-glow {
  box-shadow: 0 0 20px rgba(0, 150, 63, 0.4) !important;
}
.shadow-glow-orange {
  box-shadow: 0 0 20px rgba(255, 152, 0, 0.4) !important;
}
.border-light {
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.overflow-visible {
  overflow: visible !important;
}
.btn-hover-scale:hover {
  transform: scale(1.02);
  transition: transform 0.2s;
}

.pending-shift-review {
  background: rgba(34, 27, 23, 0.92) !important;
  border-color: rgba(255, 112, 67, 0.42) !important;
}

.pending-shift-review__title {
  background: linear-gradient(90deg, rgba(230, 81, 0, 0.16), rgba(30, 30, 30, 0));
}

.pending-shift-review__entry {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.035);
}

.pending-shift-review__entry--reviewed {
  border-color: rgba(102, 187, 106, 0.24);
}

.reviewed-shift-history {
  background: transparent !important;
}

.reviewed-shift-history__note {
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(102, 187, 106, 0.08);
  color: rgba(255, 255, 255, 0.78);
}

.reviewed-shift-history__change {
  overflow-wrap: anywhere;
}

.pending-shift-review__reason {
  padding-left: 10px;
  border-left: 3px solid rgba(255, 112, 67, 0.8);
}

.pending-shift-review__value {
  display: flex;
  min-height: 64px;
  flex-direction: column;
  justify-content: center;
  padding: 10px 12px;
  border-radius: 8px;
  line-height: 1.45;
}

.pending-shift-review__value--before {
  background: rgba(239, 83, 80, 0.09);
  border: 1px solid rgba(239, 83, 80, 0.22);
}

.pending-shift-review__value--after {
  background: rgba(102, 187, 106, 0.09);
  border: 1px solid rgba(102, 187, 106, 0.22);
}

.pending-shift-review__value-label {
  margin-bottom: 3px;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
</style>
